import { joinApiUrl } from "../../config/api-url";
import { normalizeApiError } from "./api-errors";
import { normalizeActivationPayload } from "./activation-payload";
import { Formio } from "formiojs";

const EditGridComponent =
    Formio.Components.components.editgrid;

/**
 * Liest einen Wert über einen Punktpfad.
 *
 * Beispiel:
 * getByPath(object, "api.resultPath")
 */
function getByPath(object, path) {
    if (!path) {
        return object;
    }

    return String(path)
        .split(".")
        .filter(Boolean)
        .reduce((current, key) => {
            if (
                current === null ||
                current === undefined
            ) {
                return undefined;
            }

            return current[key];
        }, object);
}

/**
 * Schreibt einen Wert über einen Punktpfad.
 *
 * Beispiel:
 * setByPath(payload, "data.groups", rows)
 */
function setByPath(object, path, value) {
    const parts = String(path || "")
        .split(".")
        .filter(Boolean);

    if (parts.length === 0) {
        return;
    }

    let current = object;

    parts.forEach((part, index) => {
        if (index === parts.length - 1) {
            current[part] = value;
            return;
        }

        if (
            current[part] === null ||
            current[part] === undefined ||
            typeof current[part] !== "object"
        ) {
            current[part] = {};
        }

        current = current[part];
    });
}

/**
 * Ersetzt Platzhalter durch Werte aus dem Form.io-Datenmodell.
 *
 * Beispiel:
 * /api/groups/{groupId}
 */
function resolveTemplate(template, data) {
    return String(template || "").replace(
        /\{([^}]+)\}/g,
        (_, key) => {
            const value = getByPath(
                data,
                key.trim()
            );

            if (
                value === null ||
                value === undefined
            ) {
                return "";
            }

            return encodeURIComponent(
                String(value)
            );
        }
    );
}



function deepClone(value) {
    if (
        value === null ||
        value === undefined
    ) {
        return value;
    }

    return JSON.parse(
        JSON.stringify(value)
    );
}

/**
 * Ergänzt ausschließlich fehlende Konfigurationswerte.
 * Bereits im Form.io-Builder gespeicherte Werte haben immer Vorrang.
 * Arrays werden nur gesetzt, wenn die Eigenschaft vollständig fehlt;
 * vorhandene Arrays werden niemals automatisch zusammengeführt.
 */
function isPlainObject(value) {
    return (
        value !== null &&
        typeof value === "object" &&
        !Array.isArray(value)
    );
}

function mergeMissingDefaults(target, defaults) {
    if (!isPlainObject(target) || !isPlainObject(defaults)) {
        return target;
    }

    Object.keys(defaults).forEach((key) => {
        const defaultValue = defaults[key];

        if (target[key] === undefined) {
            target[key] = deepClone(defaultValue);
            return;
        }

        if (
            isPlainObject(target[key]) &&
            isPlainObject(defaultValue)
        ) {
            mergeMissingDefaults(target[key], defaultValue);
        }
    });

    return target;
}


/**
 * Vergleicht zwei einfache JSON-Datenstrukturen.
 */
function valuesAreEqual(left, right) {
    try {
        return (
            JSON.stringify(left) ===
            JSON.stringify(right)
        );
    } catch (error) {
        return false;
    }
}

function normalizeAxiosError(error) {
    return normalizeApiError(error);
}

function normalizeArray(value) {
    return Array.isArray(value) ?
        value : [];
}


/**
 * Prüft, ob ein Wert tatsächlich vorhanden ist.
 * false und 0 gelten dabei als gültige Werte.
 */
function hasValue(value) {
    return (
        value !== null &&
        value !== undefined &&
        value !== ""
    );
}

/**
 * Vergleicht zwei Werte.
 */
function valuesMatch(left, right, ignoreCase = true) {
    if (
        left === null ||
        left === undefined
    ) {
        return false;
    }

    if (
        typeof right === "boolean"
    ) {
        return Boolean(left) === right;
    }

    if (
        typeof right === "number"
    ) {
        return Number(left) === right;
    }

    const normalizedLeft =
        String(left);

    const normalizedRight =
        String(right ?? "");

    if (ignoreCase !== false) {
        return (
            normalizedLeft.toLowerCase() ===
            normalizedRight.toLowerCase()
        );
    }

    return normalizedLeft === normalizedRight;
}

/**
 * Wandelt einen einzelnen Wert oder ein Array
 * in ein Array um.
 */
function normalizeList(value) {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return [];
    }

    return Array.isArray(value) ?
        value : [value];
}


/**
 * Wandelt kommagetrennte Texte, Tags oder Arrays
 * in ein bereinigtes Array um.
 */
function normalizeTextList(value) {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return [];
    }

    const entries =
        Array.isArray(value) ?
        value :
        String(value).split(",");

    return entries
        .map((entry) => {
            if (
                entry &&
                typeof entry === "object"
            ) {
                if (hasValue(entry.value)) {
                    return String(entry.value).trim();
                }

                if (hasValue(entry.label)) {
                    return String(entry.label).trim();
                }
            }

            return String(entry).trim();
        })
        .filter(Boolean);
}

/**
 * Liest Abhängigkeiten aus der neuen Textkonfiguration
 * oder aus der bisherigen Array-Konfiguration.
 */
function normalizeDependsOn(config) {
    if (
        typeof config.dependsOnText === "string" &&
        config.dependsOnText.trim() !== ""
    ) {
        return normalizeTextList(config.dependsOnText);
    }

    return normalizeTextList(config.dependsOn);
}

/**
 * Liest zusätzliche Kontextfelder aus Text oder Array.
 */
function normalizeContextFields(config) {
    if (
        typeof config.contextFieldsText === "string" &&
        config.contextFieldsText.trim() !== ""
    ) {
        return normalizeTextList(config.contextFieldsText);
    }

    return normalizeTextList(config.contextFields);
}

/**
 * Liest Feld-Mappings zwischen API-DTO und eindeutigen Form.io-Komponentenkeys.
 *
 * Beispiel:
 * api.fieldMappings = [
 *   { apiField: "CommunityId", componentField: "fbAdminCondCommunityId" }
 * ]
 */
function normalizeFieldMappings(config) {
    return normalizeArray(config && config.fieldMappings)
        .map((mapping) => {
            if (!mapping || typeof mapping !== "object") {
                return null;
            }

            const apiField = String(mapping.apiField || "").trim();
            const componentField = String(mapping.componentField || "").trim();

            if (!apiField || !componentField) {
                return null;
            }

            return { apiField, componentField };
        })
        .filter(Boolean);
}

function mapApiRowToComponentRow(row, config) {
    const mapped = deepClone(row || {});

    normalizeFieldMappings(config).forEach((mapping) => {
        setByPath(
            mapped,
            mapping.componentField,
            deepClone(getByPath(row, mapping.apiField))
        );
    });

    return mapped;
}

function mapComponentRowToApiRow(row, config) {
    const mapped = deepClone(row || {});

    normalizeFieldMappings(config).forEach((mapping) => {
        const value = getByPath(row, mapping.componentField);
        setByPath(mapped, mapping.apiField, deepClone(value));

        // Die eindeutigen Form.io-Feldnamen gehören nicht in das Backend-DTO.
        if (mapping.componentField.indexOf(".") === -1 && mapping.componentField !== mapping.apiField) {
            delete mapped[mapping.componentField];
        }
    });

    return mapped;
}

/**
 * Wandelt Builder-Vergleichswerte in passende Datentypen um.
 */
function parseConfiguredValue(value) {
    if (typeof value !== "string") {
        return value;
    }

    const trimmed = value.trim();

    if (trimmed === "true") {
        return true;
    }

    if (trimmed === "false") {
        return false;
    }

    if (trimmed !== "" && !Number.isNaN(Number(trimmed))) {
        return Number(trimmed);
    }

    return trimmed;
}




const API_EDIT_GRID_CONFIG_VERSION = 3;

function getApiEditGridConfigDefaults() {
    return {
        addAnother: "Hinzufügen",
        saveRow: "Übernehmen",
        removeRow: "Abbrechen",
        editRow: "Bearbeiten",

        rowDrafts: false,
        displayAsTable: false,
        allowAdd: true,
        allowEdit: true,
        allowDelete: true,

        editorColumnCount: 1,
        singleObjectResponse: false,
        actionOnly: false,
        directSingleAction: false,
        reloadOnDataChanged: false,

        searchEnabled: false,
        searchPlaceholder: "Datensätze durchsuchen",
        searchFieldsText: "",

        paginationEnabled: true,
        pageSize: 10,
        pageSizeOptions: [5, 10, 25, 50],
        pageSizeOptionsText: "5, 10, 25, 50",

        sortingEnabled: true,
        defaultSortField: "",
        defaultSortDirection: "asc",

        actionColumnLabel: "Aktion",
        actionOpenLabel: "Aktionen öffnen",

        emptyMessage: "Keine Datensätze vorhanden.",
        noFilterResultMessage: "Keine passenden Datensätze gefunden.",
        loadingMessage: "Daten werden geladen …",
        savingMessage: "Datensatz wird gespeichert …",
        deletingMessage: "Datensatz wird gelöscht …",
        actionRunningMessage: "Aktion wird ausgeführt …",
        loadErrorMessage: "Die Datensätze konnten nicht geladen werden.",
        noActionMessage: "Für diesen Datensatz ist derzeit keine Aktion verfügbar.",

        filtersEnabled: false,
        filtersCollapsible: true,
        filtersInitiallyOpen: false,
        showResetFiltersButton: true,
        rememberFilters: false,
        filterDefinitions: [],
        rowActions: [],

        api: {
            loadEndpoint: "",
            loadMethod: "GET",
            resultPath: "",

            createEndpoint: "",
            createMethod: "POST",
            updateEndpoint: "",
            updateMethod: "PUT",
            deleteEndpoint: "",
            deleteMethod: "DELETE",

            idField: "id",
            createPayloadPath: "",
            updatePayloadPath: "",

            dependsOnText: "",
            contextFieldsText: "",
            dependsOn: [],
            contextFields: [],
            fieldMappings: [],

            autoLoad: true,
            reloadAfterSave: true,
            reloadAfterDelete: true
        }
    };
}

export default class ApiEditGridComponent
extends EditGridComponent {

    static schema(...extend) {
        return EditGridComponent.schema({
                type: "apiEditGrid",
                label: "API Edit Grid",
                key: "apiEditGrid",

                input: true,
                persistent: false,
                tableView: false,

                addAnother: "Hinzufügen",
                saveRow: "Übernehmen",
                removeRow: "Abbrechen",
                editRow: "Bearbeiten",

                rowDrafts: false,
                displayAsTable: false,

                allowAdd: true,
                allowEdit: true,
                allowDelete: true,

                /*
                 * Anzahl der Spalten im Hinzufügen-/Bearbeiten-Editor.
                 * 1 = einspaltig, 2 = zweispaltig.
                 * Die Tabellenansicht bleibt davon unberührt.
                 */
                editorColumnCount: 1,

                /*
                 * Unterstützt GET-Endpunkte, die statt eines Arrays
                 * genau ein JSON-Objekt zurückgeben.
                 */
                singleObjectResponse: false,

                /*
                 * Sondermodus:
                 * Datensätze werden nicht über CRUD bearbeitet.
                 * Der Stift öffnet ausschließlich konfigurierte Aktionen.
                 */
                actionOnly: false,

                /*
                 * Zeigt bei genau einer fachlich sichtbaren Action
                 * diese direkt als Button an. Keine Rollenprüfung hier;
                 * Komponentenrechte werden zentral ausgewertet.
                 */
                directSingleAction: false,

                /*
                 * Optionaler Reload nach Datenänderungen anderer API-Komponenten.
                 */
                reloadOnDataChanged: false,

                /*
                 * Tabellenfunktionen für CRUD- und Action-Only-Modus.
                 */
                searchEnabled: false,
                searchPlaceholder: "Datensätze durchsuchen",
                searchFieldsText: "",

                paginationEnabled: true,
                pageSize: 10,
                pageSizeOptions: [5, 10, 25, 50],
                pageSizeOptionsText: "5, 10, 25, 50",

                /*
                 * Sortierung.
                 */
                sortingEnabled: true,
                defaultSortField: "",
                defaultSortDirection: "asc",

                /*
                 * Generische Texte der Tabellenansicht.
                 */
                actionColumnLabel: "Aktion",
                actionOpenLabel: "Aktionen öffnen",

                emptyMessage: "Keine Datensätze vorhanden.",
                noFilterResultMessage: "Keine passenden Datensätze gefunden.",
                loadingMessage: "Daten werden geladen …",
                savingMessage: "Datensatz wird gespeichert …",
                deletingMessage: "Datensatz wird gelöscht …",
                actionRunningMessage: "Aktion wird ausgeführt …",
                loadErrorMessage: "Die Datensätze konnten nicht geladen werden.",

                noActionMessage: "Für diesen Datensatz ist derzeit keine Aktion verfügbar.",

                /*
                 * Benutzerfilter.
                 */
                filtersEnabled: false,
                filtersCollapsible: true,
                filtersInitiallyOpen: false,
                showResetFiltersButton: true,
                rememberFilters: false,
                filterDefinitions: [],

                /*
                 * Konfigurierbare Aktionen je Tabellenzeile.
                 */
                rowActions: [],

                api: {
                    loadEndpoint: "",
                    loadMethod: "GET",
                    resultPath: "",

                    createEndpoint: "",
                    createMethod: "POST",

                    updateEndpoint: "",
                    updateMethod: "PUT",

                    deleteEndpoint: "",
                    deleteMethod: "DELETE",

                    idField: "id",

                    createPayloadPath: "",
                    updatePayloadPath: "",

                    dependsOnText: "",
                    contextFieldsText: "",

                    /*
                     * Alte Array-Konfiguration bleibt kompatibel.
                     */
                    dependsOn: [],
                    contextFields: [],

                    /*
                     * Mapping zwischen Backend-DTO-Feldern und global eindeutigen
                     * Form.io-Komponentenkeys innerhalb der Grid-Zeile.
                     */
                    fieldMappings: [],

                    autoLoad: true,
                    reloadAfterSave: true,
                    reloadAfterDelete: true
                }
            },
            ...extend
        );
    }

    static get builderInfo() {
        return {
            title: "API Edit Grid",
            icon: "edit",
            group: "basic",
            weight: 23,
            schema: ApiEditGridComponent.schema()
        };
    }

    constructor(
        component,
        options,
        data
    ) {
        super(
            component,
            options,
            data
        );

        this._apiLoading = false;
        this._apiSaving = false;
        this._apiDeleting = false;

        this._apiLoadedSignature = null;
        this._apiError = null;

        this._loadedRows = [];
        this._loadingPromise = null;

        /*
         * Zustände für den Action-Only-Modus.
         */
        this._actionRowIndex = null;
        this._actionRunning = false;
        this._searchValue = "";
        this._currentPage = 1;

        this._sortField =
            this.component.defaultSortField || "";

        this._sortDirection =
            this.component.defaultSortDirection === "desc" ?
            "desc" :
            "asc";

        this._filterValues = {};
        this._filtersOpen = this.component.filtersInitiallyOpen === true;
        this._filtersRestored = false;
        this._editRowIndex = null;
        this._isAdding = false;
        this._rowEditorForm = null;
        this._actionRootElement = null;
        this._dataChangedHandler = null;
        this._rootDependencyChangeHandler = null;
        this._rootDependencyChangeTimer = null;

        /*
         * Berechtigungen für einzelne RowActions.
         *
         * Die eigentliche Berechnung erfolgt weiterhin zentral im Backend
         * über /api/permission-management/effective-permissions.
         * Der Cache wird global geteilt, damit mehrere ApiEditGrids nicht
         * dieselben Berechtigungen mehrfach laden.
         */
        this._rowActionPermissionsLoading = false;
    }

    init() {
        this.upgradeComponentConfig();
        super.init();
    }

    /*
     * Aktualisiert ältere, bereits in Formularen gespeicherte
     * ApiEditGrid-Konfigurationen verlustfrei auf den aktuellen Stand.
     */
    upgradeComponentConfig() {
        const component = this.component || {};

        if (!isPlainObject(component.api)) {
            component.api = {};
        }

        let version = Number(
            component.apiEditGridConfigVersion || 0
        );

        // Bekannte Alt-Eigenschaft unabhängig vom Versionsmarker bereinigen.
        if (
            component.reloadOnDataChanged === undefined &&
            component.reloadOnPackageChanged !== undefined
        ) {
            component.reloadOnDataChanged =
                component.reloadOnPackageChanged === true;
        }

        if (
            Object.prototype.hasOwnProperty.call(
                component,
                "reloadOnPackageChanged"
            )
        ) {
            delete component.reloadOnPackageChanged;
        }

        // v0 -> v1: reloadOnPackageChanged -> reloadOnDataChanged.
        if (version < 1) {
            version = 1;
        }

        // v1 -> v2: neue Text-/Mapping-Konfigurationen ergänzen.
        if (version < 2) {
            if (component.api.dependsOnText === undefined) {
                component.api.dependsOnText = "";
            }

            if (component.api.contextFieldsText === undefined) {
                component.api.contextFieldsText = "";
            }

            if (component.api.fieldMappings === undefined) {
                component.api.fieldMappings = [];
            }

            version = 2;
        }

        // Fehlende aktuelle Defaults immer prüfen. Vorhandene Builder-Werte
        // und vorhandene Arrays werden dabei nicht verändert.
        mergeMissingDefaults(
            component,
            getApiEditGridConfigDefaults()
        );

        if (version < 3) {
            version = 3;
        }

        component.apiEditGridConfigVersion =
            Math.max(version, API_EDIT_GRID_CONFIG_VERSION);
    }

    get apiConfig() {
        return this.component.api || {};
    }

    get isBuilderMode() {
        return !!(
            window.__OZG_FORMIO_BUILDER_MODE === true ||
            (this.options && this.options.builder === true) ||
            (this.root && this.root.options && this.root.options.builder === true)
        );
    }

    getFormData() {
        let rootData = {};

        if (
            this.root &&
            this.root.data &&
            typeof this.root.data === "object"
        ) {
            rootData = this.root.data;
        } else if (
            this.root &&
            this.root.submission &&
            this.root.submission.data &&
            typeof this.root.submission.data === "object"
        ) {
            rootData = this.root.submission.data;
        }

        // Verschachtelte ApiEditGrids (z. B. Formulare innerhalb einer
        // Paketdefinition) benötigen zusätzlich den lokalen Zeilenkontext.
        // Lokale Werte überschreiben gleichnamige globale Werte.
        return {
            ...(rootData || {}),
            ...(this.data || {})
        };
    }

    get idField() {
        return (
            this.apiConfig.idField ||
            "id"
        );
    }

    get isActionOnly() {
        return this.component.actionOnly === true;
    }

    get requestSignature() {
        const config =
            this.apiConfig;

        const formData =
            this.getFormData();

        const dependsOn =
            normalizeDependsOn(config);

        return JSON.stringify({
            endpoint: resolveTemplate(
                config.loadEndpoint,
                formData
            ),

            dependsOn: dependsOn.map(
                (key) =>
                    getByPath(
                        formData,
                        key
                    )
            )
        });
    }

    shouldLoad() {
        if (this.isBuilderMode) {
            return false;
        }

        const config =
            this.apiConfig;

        if (
            config.autoLoad === false
        ) {
            return false;
        }

        if (!config.loadEndpoint) {
            return false;
        }

        const formData =
            this.getFormData();

        const dependsOn =
            normalizeDependsOn(config);

        return dependsOn.every(
            (key) => {
                const value =
                    getByPath(
                        formData,
                        key
                    );

                return (
                    value !== null &&
                    value !== undefined &&
                    value !== ""
                );
            }
        );
    }

    getAxios() {
        const axios =
            window.GLOBALAXIOS;

        if (!axios) {
            throw new Error(
                "GLOBALAXIOS ist nicht verfügbar."
            );
        }

        return axios;
    }

    getApiBaseUrl() {
        return (
            window.GLOBALCONFIG ?.API_URL ||
            ""
        );
    }

    async executeRequest(
        method,
        endpoint,
        payload,
        extraRequestConfig = {}
    ) {
        // Letzte zentrale Schranke: Im Form.io Builder niemals HTTP ausführen.
        if (this.isBuilderMode) {
            console.debug(
                "[ApiEditGrid] API-Aufruf im Builder blockiert:",
                endpoint
            );

            return {
                status: 200,
                data: []
            };
        }

        const axios =
            this.getAxios();

        const url =
            joinApiUrl(this.getApiBaseUrl(), endpoint);

        const normalizedMethod =
            String(
                method || "GET"
            ).toUpperCase();

        let response;

        const requestConfig = {
            validateStatus: function() {
                return true;
            },
            ...extraRequestConfig
        };

        switch (normalizedMethod) {
            case "POST":
                response = await axios.post(
                    url,
                    payload || {},
                    requestConfig
                );
                break;

            case "PUT":
                response = await axios.put(
                    url,
                    payload || {},
                    requestConfig
                );
                break;

            case "PATCH":
                response = await axios.patch(
                    url,
                    payload || {},
                    requestConfig
                );
                break;

            case "DELETE":
                response = await axios.delete(
                    url, {
                        ...requestConfig,
                        data: payload || undefined
                    }
                );
                break;

            case "GET":
            default:
                response = await axios.get(
                    url,
                    requestConfig
                );
                break;
        }

        if (!response ||
            response.status < 200 ||
            response.status >= 300
        ) {
            const responseData =
                response && response.data ?
                response.data : {};

            const message =
                responseData.messageDe ||
                responseData.Message ||
                responseData.message ||
                "Die Anfrage konnte nicht verarbeitet werden.";

            const error =
                new Error(message);

            error.response = response;

            throw error;
        }

        return response;
    }

    buildPayload(
        row,
        payloadPath,
        normalizeActivation = false
    ) {
        const payload = {};
        const apiRow = mapComponentRowToApiRow(
            row,
            this.apiConfig
        );

        if (payloadPath) {
            setByPath(
                payload,
                payloadPath,
                apiRow
            );
        } else {
            Object.assign(
                payload,
                apiRow
            );
        }

        const contextFields =
            normalizeContextFields(
                this.apiConfig
            );

        contextFields.forEach(
            (fieldConfig) => {
                const parts = String(fieldConfig)
                    .split(":")
                    .map((part) => part.trim())
                    .filter(Boolean);

                const sourceField = parts[0];
                const targetField = parts[1] || sourceField;

                if (!sourceField) {
                    return;
                }

                setByPath(
                    payload,
                    targetField,
                    getByPath(
                        this.data,
                        sourceField
                    )
                );
            }
        );

        if (!normalizeActivation) return payload;

        // Context fields may also provide activation settings. Normalize the
        // final API row, including wrapped payloads, after all mappings.
        const normalizedRow = normalizeActivationPayload(
            payloadPath ? getByPath(payload, payloadPath) : payload,
            this.apiConfig.activationFields
        );
        if (payloadPath) {
            setByPath(payload, payloadPath, normalizedRow);
            return payload;
        }
        return normalizedRow;
    }

    setApiError(error) {
        this._apiError =
            error ?
            normalizeAxiosError(error) :
            null;

        if (
            this._apiError
        ) {
            console.warn(
                "[ApiEditGrid]",
                this._apiError,
                error
            );
        }

        this.redraw();
    }

    clearApiError() {
        this._apiError = null;
    }

    render(content) {
        const status = [];

        if (this._apiLoading) {
            status.push(`
        <div class="alert alert-info">
          ${this.escapeHtml(
            this.component.loadingMessage ||
            "Daten werden geladen …"
          )}
        </div>
      `);
        } else if (this._apiSaving) {
            status.push(`
        <div class="alert alert-info">
          ${this.escapeHtml(
            this.component.savingMessage ||
            "Datensatz wird gespeichert …"
          )}
        </div>
      `);
        } else if (this._apiDeleting) {
            status.push(`
        <div class="alert alert-info">
          ${this.escapeHtml(
            this.component.deletingMessage ||
            "Datensatz wird gelöscht …"
          )}
        </div>
      `);
        } else if (this._actionRunning) {
            status.push(`
        <div class="alert alert-info">
          ${this.escapeHtml(
            this.component.actionRunningMessage ||
            "Aktion wird ausgeführt …"
          )}
        </div>
      `);
        }

        if (this._apiError) {
            status.push(`
        <div class="alert alert-danger">
          ${this.escapeHtml(this._apiError)}
        </div>
      `);
        }

        return super.render(`
      <div
        class="api-edit-grid api-edit-grid-unified${
          this.isActionOnly
            ? " api-edit-grid-action-only"
            : " api-edit-grid-crud"
        }"
        ref="gridRoot"
      >
        ${status.join("")}

        <div ref="controlsRoot">
          ${this.renderUnifiedControls()}
        </div>

        <div class="table-responsive">
          <table class="table table-striped table-bordered table-hover">
            <thead ref="tableHead"></thead>
            <tbody ref="tableBody"></tbody>
          </table>
        </div>

        <div
          ref="pagination"
          class="d-flex justify-content-between align-items-center mt-2"
        ></div>
      </div>
    `);
    }

    /**
     * Meldet anderen API-Komponenten eine fachlich neutrale Datenänderung.
     * Der Event enthält keine Kenntnis über Pakete, Formulare oder andere Domänen.
     */
    emitDataChanged(action, row) {
        if (
            this.isBuilderMode ||
            !this.root ||
            typeof this.root.emit !== "function"
        ) {
            return;
        }

        this.root.emit("ozgApiDataChanged", {
            source: this.component.key,
            action: action || "changed",
            row: deepClone(row)
        });
    }

    escapeHtml(value) {
        return String(
                value === null || value === undefined ?
                "" :
                value
            )
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }

    attach(element) {
        const attached = super.attach(element);

        this.loadRefs(element, {
            gridRoot: "single",
            controlsRoot: "single",
            tableHead: "single",
            tableBody: "single",
            pagination: "single",
            searchInput: "single",
            pageSizeSelect: "single",
            filterToggle: "single",
            filterPanel: "single",
            filterReset: "single",
            addButton: "single"
        });

        this.bindUnifiedControls();
        this.restoreFilterState();

        window.setTimeout(() => {
            if (!this.isBuilderMode) {
                this.loadApiRows(false);
                this.ensureRowActionPermissionsLoaded();
            }

            this.renderUnifiedTable();
        }, 0);

        if (
            !this.isBuilderMode &&
            this.component.reloadOnDataChanged === true &&
            this.root &&
            typeof this.root.on === "function" &&
            !this._dataChangedHandler
        ) {
            this._dataChangedHandler = (eventData) => {
                if (eventData && eventData.source === this.component.key) {
                    return;
                }

                this._apiLoadedSignature = null;
                window.setTimeout(() => this.loadApiRows(true), 0);
            };

            this.root.on("ozgApiDataChanged", this._dataChangedHandler);
        }

        /*
         * Änderungen externer Form.io-Abhängigkeiten beobachten.
         * Dadurch funktionieren dependsOn-Felder auch außerhalb des EditGrids.
         */
        if (
            !this.isBuilderMode &&
            normalizeDependsOn(this.apiConfig).length > 0 &&
            this.root &&
            typeof this.root.on === "function" &&
            !this._rootDependencyChangeHandler
        ) {
            this._rootDependencyChangeHandler = () => {
                window.clearTimeout(this._rootDependencyChangeTimer);

                this._rootDependencyChangeTimer = window.setTimeout(() => {
                    this._rootDependencyChangeTimer = null;

                    const nextSignature = this.requestSignature;

                    if (
                        nextSignature === this._apiLoadedSignature ||
                        this._apiLoading ||
                        this._apiSaving ||
                        this._apiDeleting
                    ) {
                        return;
                    }

                    this._apiLoadedSignature = null;

                    if (!this.shouldLoad()) {
                        this._loadedRows = [];
                        this.setValue([], {
                            modified: false,
                            noUpdateEvent: true,
                            fromApi: true
                        });
                        this.refreshUnifiedControls();
                        this.renderUnifiedTable();
                        return;
                    }

                    this.loadApiRows(true);
                }, 100);
            };

            this.root.on("change", this._rootDependencyChangeHandler);
        }

        return attached;
    }

    renderUnifiedControls() {
            const searchHtml =
                this.component.searchEnabled === true ?
                `
          <div class="col-12 col-md-5 col-lg-4 mb-3">
            <label class="mb-1">Suche</label>
            <input
              type="search"
              ref="searchInput"
              class="form-control"
              placeholder="${this.escapeHtml(
                this.component.searchPlaceholder ||
                "Datensätze durchsuchen"
              )}"
              value="${this.escapeHtml(this._searchValue || "")}"
            >
          </div>
        ` :
                "";

            const pageSizeOptions = this.getPageSizeOptions();
            const pageSizeHtml =
                this.component.paginationEnabled !== false &&
                pageSizeOptions.length > 1 ?
                `
          <div class="col-12 col-md-3 col-lg-2 mb-3">
            <label class="mb-1">Einträge pro Seite</label>
            <select ref="pageSizeSelect" class="form-control">
              ${pageSizeOptions
                .map((value) => `
                  <option
                    value="${this.escapeHtml(value)}"
                    ${Number(value) === this.getPageSize() ? " selected" : ""}
                  >
                    ${this.escapeHtml(value)}
                  </option>
                `)
                .join("")}
            </select>
          </div>
        `
        : "";

    const filterDefinitions = this.getFilterDefinitions();
    const filterHtml =
      this.component.filtersEnabled === true &&
      filterDefinitions.length > 0
        ? `
          <div class="mt-3">
            ${
              this.component.filtersCollapsible !== false
                ? `
                  <button
                    type="button"
                    ref="filterToggle"
                    class="btn btn-outline-primary btn-sm mb-2"
                  >
                    Filter ${this._filtersOpen ? "ausblenden" : "anzeigen"}
                    ${this.getActiveFilterCount() > 0
                      ? `(${this.getActiveFilterCount()} aktiv)`
                      : ""}
                  </button>
                `
                : ""
            }

            <div
              ref="filterPanel"
              style="${
                this.component.filtersCollapsible !== false &&
                !this._filtersOpen
                  ? "display:none;"
                  : ""
              }"
            >
              <div class="card card-body mb-3">
                <div class="row">
                  ${filterDefinitions
                    .map((definition, index) =>
                      this.renderFilterControl(definition, index)
                    )
                    .join("")}
                </div>

                ${
                  this.component.showResetFiltersButton !== false
                    ? `
                      <div>
                        <button
                          type="button"
                          ref="filterReset"
                          class="btn btn-secondary btn-sm"
                        >
                          Filter zurücksetzen
                        </button>
                      </div>
                    `
                    : ""
                }
              </div>
            </div>
          </div>
        `
        : "";

    const addButtonHtml =
      this.component.allowAdd !== false
        ? `
          <div class="mt-3">
            <button
              type="button"
              ref="addButton"
              class="btn btn-primary"
            >
              ${this.escapeHtml(
                this.component.addAnother || "Hinzufügen"
              )}
            </button>
          </div>
        `
        : "";

    return `
      <div class="api-edit-grid-controls mb-3">
        <div class="row align-items-end">
          ${searchHtml}
          ${pageSizeHtml}
        </div>
        ${filterHtml}
        ${addButtonHtml}
      </div>
    `;
  }

  refreshUnifiedControls() {
    if (!this.refs.controlsRoot) {
      return;
    }

    this.refs.controlsRoot.innerHTML =
      this.renderUnifiedControls();

    this.loadRefs(this.element, {
      searchInput: "single",
      pageSizeSelect: "single",
      filterToggle: "single",
      filterPanel: "single",
      filterReset: "single",
      addButton: "single"
    });

    this.bindUnifiedControls();
  }

  bindUnifiedControls() {
    if (this.refs.searchInput) {
      this.addEventListener(
        this.refs.searchInput,
        "input",
        (event) => {
          this._searchValue = event.target.value || "";
          this.resetTablePosition();
          this.renderUnifiedTable();
        }
      );
    }

    if (this.refs.pageSizeSelect) {
      this.addEventListener(
        this.refs.pageSizeSelect,
        "change",
        (event) => {
          const pageSize = Number(event.target.value);
          if (pageSize > 0) {
            this.component.pageSize = pageSize;
            this.resetTablePosition();
            this.renderUnifiedTable();
          }
        }
      );
    }

    if (this.refs.filterToggle) {
      this.addEventListener(
        this.refs.filterToggle,
        "click",
        () => {
          this._filtersOpen = !this._filtersOpen;
          this.redraw();
        }
      );
    }

    if (this.refs.filterReset) {
      this.addEventListener(
        this.refs.filterReset,
        "click",
        () => {
          this._filterValues = {};
          this.saveFilterState();
          this.resetTablePosition();
          this.redraw();
        }
      );
    }

    if (this.refs.addButton) {
      this.addEventListener(
        this.refs.addButton,
        "click",
        () => this.openCrudEditor(null)
      );
    }

    if (this.refs.filterPanel) {
      this.refs.filterPanel
        .querySelectorAll("[data-filter-key]")
        .forEach((input) => {
          const eventName =
            input.tagName === "INPUT" &&
            ["text", "search", "number", "date"].includes(input.type)
              ? "input"
              : "change";

          this.addEventListener(input, eventName, () => {
            this.readFilterValueFromControl(input);
            this.saveFilterState();
            this.resetTablePosition();
            this.renderUnifiedTable();
          });
        });
    }
  }

  resetTablePosition() {
    this._currentPage = 1;
    this._actionRowIndex = null;
    this._editRowIndex = null;
    this._isAdding = false;
    this.destroyRowEditor();
  }

  getTableComponents() {
    return normalizeArray(this.component.components).filter(
      (component) =>
        component &&
        component.tableView !== false &&
        component.type !== "hidden"
    );
  }

  /**
   * Baut ausschließlich für den geöffneten Zeileneditor ein
   * ein- oder zweispaltiges Layout. Die Tabellenkomponenten bleiben unverändert.
   */
  buildRowEditorComponents() {
    const source = deepClone(
      this.component.components || []
    );

    const columnCount = Number(
      this.component.editorColumnCount || 1
    );

    if (columnCount !== 2) {
      return source;
    }

    const hiddenComponents = [];
    const visibleComponents = [];

    source.forEach((component) => {
      if (!component) {
        return;
      }

      if (
        component.type === "hidden" ||
        component.hidden === true
      ) {
        hiddenComponents.push(component);
        return;
      }

      visibleComponents.push(component);
    });

    const result = [...hiddenComponents];

    for (let index = 0; index < visibleComponents.length; index += 2) {
      const left = visibleComponents[index];
      const right = visibleComponents[index + 1];

      result.push({
        type: "columns",
        key: "__apiEditGridEditorColumns_" + (index / 2),
        input: false,
        tableView: false,
        columns: [
          {
            size: "md",
            width: 6,
            offset: 0,
            push: 0,
            pull: 0,
            currentWidth: 6,
            components: left ? [left] : []
          },
          {
            size: "md",
            width: 6,
            offset: 0,
            push: 0,
            pull: 0,
            currentWidth: 6,
            components: right ? [right] : []
          }
        ]
      });
    }

    return result;
  }

  getFilterDefinitions() {
    return normalizeArray(this.component.filterDefinitions).filter(
      (definition) =>
        definition &&
        String(definition.key || "").trim() !== "" &&
        String(definition.field || "").trim() !== ""
    );
  }

  getActiveFilterCount() {
    return Object.values(this._filterValues || {}).filter((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (value && typeof value === "object") {
        return Object.values(value).some(hasValue);
      }

      return hasValue(value);
    }).length;
  }

  renderFilterControl(definition, index) {
    const key = String(definition.key);
    const value = this._filterValues[key];
    const type = definition.type || "text";
    const label = definition.label || definition.field;
    const common = `data-filter-key="${this.escapeHtml(key)}"`;

    let control = "";

    if (type === "select" || type === "multiselect") {
      const options = this.getFilterOptions(definition);
      const selectedValues = Array.isArray(value)
        ? value.map(String)
        : [String(value ?? "")];

      control = `
        <select
          class="form-control"
          ${common}
          ${type === "multiselect" ? "multiple" : ""}
        >
          ${type === "select" ? `<option value="">Alle</option>` : ""}
          ${options
            .map((option) => {
              const optionValue = String(option.value ?? "");
              return `
                <option
                  value="${this.escapeHtml(optionValue)}"
                  ${selectedValues.includes(optionValue) ? " selected" : ""}
                >
                  ${this.escapeHtml(option.label ?? optionValue)}
                </option>
              `;
            })
            .join("")}
        </select>
      `;
    } else if (type === "boolean") {
      control = `
        <select class="form-control" ${common}>
          <option value="">Alle</option>
          <option value="true" ${value === true ? " selected" : ""}>Ja</option>
          <option value="false" ${value === false ? " selected" : ""}>Nein</option>
        </select>
      `;
    } else if (type === "numberRange" || type === "dateRange") {
      const current = value && typeof value === "object" ? value : {};
      const inputType = type === "dateRange" ? "date" : "number";
      control = `
        <div class="d-flex">
          <input
            type="${inputType}"
            class="form-control mr-2"
            data-filter-key="${this.escapeHtml(key)}"
            data-filter-part="from"
            placeholder="Von"
            value="${this.escapeHtml(current.from ?? "")}"
          >
          <input
            type="${inputType}"
            class="form-control"
            data-filter-key="${this.escapeHtml(key)}"
            data-filter-part="to"
            placeholder="Bis"
            value="${this.escapeHtml(current.to ?? "")}"
          >
        </div>
      `;
    } else {
      const inputType =
        type === "number"
          ? "number"
          : type === "date"
            ? "date"
            : "text";

      control = `
        <input
          type="${inputType}"
          class="form-control"
          ${common}
          value="${this.escapeHtml(value ?? "")}"
          placeholder="${this.escapeHtml(definition.placeholder || "")}"
        >
      `;
    }

    return `
      <div class="col-md-4 mb-3" data-filter-index="${index}">
        <label>${this.escapeHtml(label)}</label>
        ${control}
      </div>
    `;
  }

  getFilterOptions(definition) {
    const fixed = normalizeArray(definition.options)
      .map((option) => {
        if (option && typeof option === "object") {
          return {
            value: option.value,
            label: option.label ?? option.value
          };
        }

        return { value: option, label: option };
      });

    if (fixed.length > 0) {
      return fixed;
    }

    if (typeof definition.valuesText === "string" && definition.valuesText.trim()) {
      return normalizeTextList(definition.valuesText).map((value) => ({
        value,
        label: value
      }));
    }

    const unique = new Map();
    normalizeArray(this.dataValue).forEach((row) => {
      const value = getByPath(row, definition.field);
      if (!hasValue(value)) {
        return;
      }

      const key = String(value);
      if (!unique.has(key)) {
        unique.set(key, {
          value,
          label: this.getMappedFilterLabel(definition, value)
        });
      }
    });

    return Array.from(unique.values()).sort((left, right) =>
      String(left.label).localeCompare(String(right.label), "de")
    );
  }

  getMappedFilterLabel(definition, value) {
    const mappings = normalizeArray(definition.valueLabels);
    const match = mappings.find((entry) =>
      valuesMatch(entry?.value, value, definition.ignoreCase !== false)
    );

    if (match) {
      return match.label;
    }

    if (String(definition.field).toLowerCase() === "status") {
      return this.getStatusText(value);
    }

    return String(value);
  }

  readFilterValueFromControl(input) {
    const key = input.dataset.filterKey;
    const part = input.dataset.filterPart;
    const definition = this.getFilterDefinitions().find(
      (item) => String(item.key) === String(key)
    );

    if (!definition) {
      return;
    }

    if (part) {
      const current =
        this._filterValues[key] &&
        typeof this._filterValues[key] === "object"
          ? { ...this._filterValues[key] }
          : {};

      current[part] = input.value;
      this._filterValues[key] = current;
      return;
    }

    if (definition.type === "multiselect") {
      this._filterValues[key] = Array.from(input.selectedOptions)
        .map((option) => option.value)
        .filter(Boolean);
      return;
    }

    if (definition.type === "boolean") {
      this._filterValues[key] =
        input.value === ""
          ? ""
          : input.value === "true";
      return;
    }

    this._filterValues[key] = input.value;
  }

  rowMatchesFilter(row, definition, filterValue) {
    if (
      !hasValue(filterValue) &&
      !(filterValue && typeof filterValue === "object") &&
      !Array.isArray(filterValue)
    ) {
      return true;
    }

    const currentValue = getByPath(row, definition.field);
    const ignoreCase = definition.ignoreCase !== false;

    switch (definition.type) {
      case "select":
        return !hasValue(filterValue) ||
          valuesMatch(currentValue, filterValue, ignoreCase);

      case "multiselect":
        return !Array.isArray(filterValue) ||
          filterValue.length === 0 ||
          filterValue.some((expected) =>
            valuesMatch(currentValue, expected, ignoreCase)
          );

      case "boolean":
        return filterValue === "" ||
          Boolean(currentValue) === Boolean(filterValue);

      case "number":
        return !hasValue(filterValue) ||
          Number(currentValue) === Number(filterValue);

      case "numberRange": {
        const range = filterValue || {};
        const number = Number(currentValue);
        if (Number.isNaN(number)) {
          return false;
        }
        return (
          (!hasValue(range.from) || number >= Number(range.from)) &&
          (!hasValue(range.to) || number <= Number(range.to))
        );
      }

      case "date": {
        if (!hasValue(filterValue)) {
          return true;
        }
        const currentDate = this.normalizeDateOnly(currentValue);
        return currentDate === String(filterValue);
      }

      case "dateRange": {
        const range = filterValue || {};
        const currentDate = this.normalizeDateOnly(currentValue);
        if (!currentDate) {
          return false;
        }
        return (
          (!hasValue(range.from) || currentDate >= String(range.from)) &&
          (!hasValue(range.to) || currentDate <= String(range.to))
        );
      }

      case "text":
      default: {
        if (!hasValue(filterValue)) {
          return true;
        }

        let left = String(currentValue ?? "");
        let right = String(filterValue);
        if (ignoreCase) {
          left = left.toLowerCase();
          right = right.toLowerCase();
        }

        switch (definition.operator || "contains") {
          case "startsWith":
            return left.startsWith(right);
          case "endsWith":
            return left.endsWith(right);
          case "equals":
            return left === right;
          case "notEquals":
            return left !== right;
          case "contains":
          default:
            return left.includes(right);
        }
      }
    }
  }

  normalizeDateOnly(value) {
    if (!hasValue(value)) {
      return "";
    }

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.substring(0, 10);
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toISOString().substring(0, 10);
  }

  getSearchFields() {
    const configured =
      normalizeTextList(
        this.component.searchFieldsText
      );

    if (configured.length > 0) {
      return configured;
    }

    return this.getTableComponents()
      .map((component) =>
        component && component.key
          ? String(component.key)
          : ""
      )
      .filter(Boolean);
  }

  getFilteredRows() {
    let entries = normalizeArray(this.dataValue).map(
      (row, originalIndex) => ({ row, originalIndex })
    );

    if (this.component.filtersEnabled === true) {
      const definitions = this.getFilterDefinitions();
      entries = entries.filter((entry) =>
        definitions.every((definition) =>
          this.rowMatchesFilter(
            entry.row,
            definition,
            this._filterValues[definition.key]
          )
        )
      );
    }

    const search = String(this._searchValue || "")
      .trim()
      .toLowerCase();

    if (
      this.component.searchEnabled === true &&
      search
    ) {
      const searchFields =
        this.getSearchFields();

      entries = entries.filter((entry) =>
        searchFields.some((field) =>
          String(
            getByPath(entry.row, field) ?? ""
          )
            .toLowerCase()
            .includes(search)
        )
      );
    }

    if (
      this.component.sortingEnabled !== false &&
      this._sortField
    ) {
      const direction =
        this._sortDirection === "desc"
          ? -1
          : 1;

      entries.sort((left, right) => {
        const leftValue =
          getByPath(
            left.row,
            this._sortField
          );

        const rightValue =
          getByPath(
            right.row,
            this._sortField
          );

        if (leftValue === rightValue) {
          return 0;
        }

        if (
          leftValue === null ||
          leftValue === undefined
        ) {
          return -1 * direction;
        }

        if (
          rightValue === null ||
          rightValue === undefined
        ) {
          return 1 * direction;
        }

        const leftNumber =
          Number(leftValue);

        const rightNumber =
          Number(rightValue);

        if (
          !Number.isNaN(leftNumber) &&
          !Number.isNaN(rightNumber)
        ) {
          return (
            leftNumber - rightNumber
          ) * direction;
        }

        return String(leftValue)
          .localeCompare(
            String(rightValue),
            "de",
            {
              numeric: true,
              sensitivity: "base"
            }
          ) * direction;
      });
    }

    return entries;
  }

  getPageSizeOptions() {
    const source =
      typeof this.component.pageSizeOptionsText === "string" &&
      this.component.pageSizeOptionsText.trim() !== ""
        ? normalizeTextList(this.component.pageSizeOptionsText)
        : normalizeTextList(this.component.pageSizeOptions);

    return Array.from(
      new Set(
        source
          .map((value) => Number(value))
          .filter((value) => value > 0)
      )
    );
  }

  getPageSize() {
    const value = Number(this.component.pageSize);
    return value > 0 ? value : 10;
  }

  getPagedRows() {
    const filtered = this.getFilteredRows();

    if (this.component.paginationEnabled === false) {
      return {
        filtered,
        pageCount: 1,
        pageRows: filtered
      };
    }

    const pageSize = this.getPageSize();
    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    this._currentPage = Math.min(Math.max(this._currentPage || 1, 1), pageCount);
    const start = (this._currentPage - 1) * pageSize;

    return {
      filtered,
      pageCount,
      pageRows: filtered.slice(start, start + pageSize)
    };
  }

  /**
   * Liest statische bzw. von apiSelect zur Laufzeit geladene
   * Auswahlwerte einer Zeilenkomponente.
   */
  getComponentDisplayOptions(component) {
    if (!component || typeof component !== "object") {
      return [];
    }

    if (
      component.data &&
      Array.isArray(component.data.values)
    ) {
      return component.data.values;
    }

    if (Array.isArray(component.values)) {
      return component.values;
    }

    return [];
  }

  /**
   * Löst technische Select-Werte für die geschlossene
   * Tabellenansicht in ihre Labels auf.
   *
   * Unterstützt normale Form.io-Selects und apiSelect.
   * Das Datenmodell selbst bleibt unverändert.
   */
  resolveComponentDisplayValue(value, component) {
    if (!component) {
      return value;
    }

    const type =
      String(component.type || "").toLowerCase();

    if (
      type !== "select" &&
      type !== "apiselect"
    ) {
      return value;
    }

    const options =
      this.getComponentDisplayOptions(component);

    if (options.length === 0) {
      return value;
    }

    const resolveOne = (currentValue) => {
      const match = options.find((option) => {
        if (!option || typeof option !== "object") {
          return false;
        }

        return String(option.value) ===
          String(currentValue);
      });

      if (!match) {
        return currentValue;
      }

      return hasValue(match.label)
        ? match.label
        : currentValue;
    };

    if (Array.isArray(value)) {
      return value
        .map(resolveOne)
        .filter((entry) => hasValue(entry))
        .join(", ");
    }

    return resolveOne(value);
  }

  formatActionCellValue(value, component) {
    if (!hasValue(value)) {
      return "–";
    }

    if (typeof value === "boolean") {
      return value ? "Ja" : "Nein";
    }

    if (
      typeof value === "string" &&
      /^\d{4}-\d{2}-\d{2}T/.test(value)
    ) {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString("de-DE");
      }
    }

    const key = String(component?.key || "").toLowerCase();
    if (
      key.includes("price") ||
      key.includes("netto") ||
      key.includes("brutto")
    ) {
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric.toLocaleString("de-DE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
    }

    if (Array.isArray(value)) {
      return value.join(", ");
    }

    if (value && typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }

    return String(value);
  }

  getStatusText(value) {
    const statusTexts = {
      requested: "Freigabe ausstehend",
      scheduled: "Aktivierung geplant",
      active: "Aktiv",
      cancellation_scheduled: "Kündigung vorgemerkt",
      non_renewing: "Automatische Verlängerung beendet",
      suspended: "Gesperrt",
      expired: "Abgelaufen",
      rejected: "Abgelehnt"
    };

    return statusTexts[value] || value || "–";
  }

  getDisplayCellValue(row, component) {
    const value = getByPath(row, component.key);

    if (String(component.key).toLowerCase() === "status") {
      return this.getStatusText(value);
    }

    const resolvedValue =
      this.resolveComponentDisplayValue(
        value,
        component
      );

    return this.formatActionCellValue(
      resolvedValue,
      component
    );
  }

  renderUnifiedTable() {
    if (!this.refs.tableHead || !this.refs.tableBody) {
      return;
    }

    const components = this.getTableComponents();
    const showActionColumn =
      this.isActionOnly ||
      this.component.allowEdit !== false ||
      this.component.allowDelete !== false;

    this.refs.tableHead.innerHTML = `
      <tr>
        ${components
          .map((component) => {
            const field =
              component.key || "";

            const sortable =
              this.component.sortingEnabled !== false &&
              field !== "";

            const indicator =
              this._sortField === field
                ? this._sortDirection === "desc"
                  ? " ↓"
                  : " ↑"
                : "";

            return `
              <th>
                ${
                  sortable
                    ? `
                      <button
                        type="button"
                        class="btn btn-link p-0 font-weight-bold text-dark api-edit-grid-sort"
                        data-sort-field="${this.escapeHtml(field)}"
                      >
                        ${this.escapeHtml(
                          component.label || field
                        )}${indicator}
                      </button>
                    `
                    : this.escapeHtml(
                        component.label || field
                      )
                }
              </th>
            `;
          })
          .join("")}
        ${
          showActionColumn
            ? `
              <th class="text-center" style="width:110px;">
                ${this.escapeHtml(
                  this.component.actionColumnLabel || "Aktion"
                )}
              </th>
            `
            : ""
        }
      </tr>
    `;

    const result = this.getPagedRows();
    const rowsHtml = [];

    if (result.pageRows.length === 0 && !this._isAdding) {
      rowsHtml.push(`
        <tr>
          <td
            colspan="${components.length + (showActionColumn ? 1 : 0)}"
            class="text-center text-muted"
          >
            ${this.escapeHtml(
              normalizeArray(this.dataValue).length === 0
                ? (
                    this.component.emptyMessage ||
                    "Keine Datensätze vorhanden."
                  )
                : (
                    this.component.noFilterResultMessage ||
                    "Keine passenden Datensätze gefunden."
                  )
            )}
          </td>
        </tr>
      `);
    }

    if (this._isAdding) {
      rowsHtml.push(this.renderCrudEditorRow(null, components, showActionColumn));
    }

    result.pageRows.forEach((entry) => {
      const row = entry.row;
      const rowIndex = entry.originalIndex;

      rowsHtml.push(`
        <tr>
          ${components
            .map((component) => `
              <td>
                ${this.escapeHtml(this.getDisplayCellValue(row, component))}
              </td>
            `)
            .join("")}
          ${
            showActionColumn
              ? `<td class="text-center">${this.renderRowButtons(row, rowIndex)}</td>`
              : ""
          }
        </tr>
      `);

      if (this.isActionOnly && this._actionRowIndex === rowIndex) {
        rowsHtml.push(`
          <tr class="api-edit-grid-action-row">
            <td colspan="${components.length + (showActionColumn ? 1 : 0)}">
              ${this.renderRowActions(row, rowIndex)}
            </td>
          </tr>
        `);
      }

      if (!this.isActionOnly && this._editRowIndex === rowIndex) {
        rowsHtml.push(
          this.renderCrudEditorRow(rowIndex, components, showActionColumn)
        );
      }
    });

    this.refs.tableBody.innerHTML = rowsHtml.join("");
    this.bindSortButtons();
    this.bindTableButtons();
    this.renderPagination(result);

    if (this._isAdding || this._editRowIndex !== null) {
      window.setTimeout(() => this.mountRowEditor(), 0);
    }
  }

  bindSortButtons() {
    if (!this.refs.tableHead) {
      return;
    }

    this.refs.tableHead
      .querySelectorAll(
        ".api-edit-grid-sort"
      )
      .forEach((button) => {
        this.addEventListener(
          button,
          "click",
          () => {
            const field =
              button.dataset.sortField;

            if (!field) {
              return;
            }

            if (this._sortField === field) {
              this._sortDirection =
                this._sortDirection === "asc"
                  ? "desc"
                  : "asc";
            } else {
              this._sortField = field;
              this._sortDirection = "asc";
            }

            this.resetTablePosition();
            this.renderUnifiedTable();
          }
        );
      });
  }

  renderRowButtons(row, rowIndex) {
    if (this.isActionOnly) {
      const visibleActions = normalizeArray(this.component.rowActions)
        .filter((action) => this.isRowActionVisible(row, action));

      if (
        this.component.directSingleAction === true &&
        this.component.showRowDetails !== true &&
        visibleActions.length === 1
      ) {
        const action = visibleActions[0];

        return `
          <button
            type="button"
            class="btn btn-${this.escapeHtml(action.theme || "primary")} btn-sm api-edit-grid-direct-row-action"
            data-row-index="${rowIndex}"
            title="${this.escapeHtml(action.label || "Aktion ausführen")}"
            ${this.isRowActionDisabled(action) ? "disabled" : ""}
            aria-disabled="${this.isRowActionDisabled(action) ? "true" : "false"}"
          >
            ${this.escapeHtml(action.label || "Aktion ausführen")}
          </button>
        `;
      }

      if (
        this.component.directSingleAction === true &&
        this.component.showRowDetails !== true &&
        visibleActions.length === 0
      ) {
        return `
          <span class="text-muted">
            ${this.escapeHtml(
              this.getNoActionMessage(row)
            )}
          </span>
        `;
      }

      return `
        <button
          type="button"
          class="btn btn-light btn-sm api-edit-grid-open-actions"
          data-row-index="${rowIndex}"
          title="${this.escapeHtml(this.component.actionOpenLabel || "Aktionen öffnen")}"
          aria-label="${this.escapeHtml(this.component.actionOpenLabel || "Aktionen öffnen")}"
        >
          <i class="fa fa-pencil"></i>
        </button>
      `;
    }

    return `
      ${
        this.component.allowEdit !== false
          ? `
            <button
              type="button"
              class="btn btn-light btn-sm api-edit-grid-edit-row mr-1"
              data-row-index="${rowIndex}"
              title="${this.escapeHtml(this.component.editRow || "Bearbeiten")}"
            >
              <i class="fa fa-pencil"></i>
            </button>
          `
          : ""
      }
      ${
        this.component.allowDelete !== false
          ? `
            <button
              type="button"
              class="btn btn-danger btn-sm api-edit-grid-delete-row"
              data-row-index="${rowIndex}"
              title="Löschen"
            >
              <i class="fa fa-trash"></i>
            </button>
          `
          : ""
      }
    `;
  }

  renderCrudEditorRow(rowIndex, components, showActionColumn) {
    return `
      <tr class="api-edit-grid-editor-row">
        <td colspan="${components.length + (showActionColumn ? 1 : 0)}">
          <div class="card card-body">
            <div data-row-editor-container></div>
            <div class="mt-3">
              <button
                type="button"
                class="btn btn-primary api-edit-grid-save-editor mr-2"
              >
                ${this.escapeHtml(this.component.saveRow || "Übernehmen")}
              </button>
              <button
                type="button"
                class="btn btn-secondary api-edit-grid-cancel-editor"
              >
                ${this.escapeHtml(this.component.removeRow || "Abbrechen")}
              </button>
            </div>
          </div>
        </td>
      </tr>
    `;
  }

  bindTableButtons() {
    if (!this.refs.tableBody) {
      return;
    }

    this.refs.tableBody
      .querySelectorAll(".api-edit-grid-direct-row-action")
      .forEach((button) => {
        this.addEventListener(button, "click", () => {
          const rowIndex = Number(button.dataset.rowIndex);
          const row = this.dataValue?.[rowIndex];

          if (!row) {
            this.setApiError(new Error("Der ausgewählte Datensatz wurde nicht gefunden."));
            return;
          }

          const visibleActions = normalizeArray(this.component.rowActions)
            .filter((action) => this.isRowActionVisible(row, action));

          if (visibleActions.length !== 1) {
            this.renderUnifiedTable();
            return;
          }

          this.executeRowAction(rowIndex, visibleActions[0]);
        });
      });

    this.refs.tableBody
      .querySelectorAll(".api-edit-grid-open-actions")
      .forEach((button) => {
        this.addEventListener(button, "click", () => {
          const rowIndex = Number(button.dataset.rowIndex);
          this._actionRowIndex =
            this._actionRowIndex === rowIndex ? null : rowIndex;
          this.renderUnifiedTable();
        });
      });

    this.refs.tableBody
      .querySelectorAll(".api-edit-grid-edit-row")
      .forEach((button) => {
        this.addEventListener(button, "click", () =>
          this.openCrudEditor(Number(button.dataset.rowIndex))
        );
      });

    this.refs.tableBody
      .querySelectorAll(".api-edit-grid-delete-row")
      .forEach((button) => {
        this.addEventListener(button, "click", () =>
          this.deleteCrudRow(Number(button.dataset.rowIndex))
        );
      });

    this.refs.tableBody
      .querySelectorAll(".api-edit-grid-close-action-row")
      .forEach((button) => {
        this.addEventListener(button, "click", () => {
          this._actionRowIndex = null;
          this.renderUnifiedTable();
        });
      });

    this.refs.tableBody
      .querySelectorAll(".api-edit-grid-row-action")
      .forEach((button) => {
        this.addEventListener(button, "click", () => {
          const rowIndex = Number(button.dataset.rowIndex);
          const visibleActions = normalizeArray(this.component.rowActions)
            .filter((action) =>
              this.isRowActionVisible(this.dataValue?.[rowIndex], action)
            );
          this.executeRowAction(
            rowIndex,
            visibleActions[Number(button.dataset.actionIndex)]
          );
        });
      });

    const saveButton = this.refs.tableBody.querySelector(
      ".api-edit-grid-save-editor"
    );
    if (saveButton) {
      this.addEventListener(saveButton, "click", () => this.saveCrudEditor());
    }

    const cancelButton = this.refs.tableBody.querySelector(
      ".api-edit-grid-cancel-editor"
    );
    if (cancelButton) {
      this.addEventListener(cancelButton, "click", () => this.closeCrudEditor());
    }
  }

  renderPagination(result) {
    if (!this.refs.pagination) {
      return;
    }

    const filtered = result.filtered || [];
    const pageCount = result.pageCount || 1;
    const currentPage = this._currentPage || 1;

    if (this.component.paginationEnabled === false || pageCount <= 1) {
      this.refs.pagination.innerHTML = `
        <div class="text-muted">
          ${filtered.length} ${filtered.length === 1 ? "Eintrag" : "Einträge"}
        </div>
      `;
      return;
    }

    this.refs.pagination.innerHTML = `
      <div class="text-muted">
        ${filtered.length} ${filtered.length === 1 ? "Eintrag" : "Einträge"}
      </div>
      <div class="btn-group">
        <button
          type="button"
          class="btn btn-light btn-sm api-edit-grid-page-previous"
          ${currentPage <= 1 ? "disabled" : ""}
        >Zurück</button>
        <button type="button" class="btn btn-light btn-sm" disabled>
          Seite ${currentPage} von ${pageCount}
        </button>
        <button
          type="button"
          class="btn btn-light btn-sm api-edit-grid-page-next"
          ${currentPage >= pageCount ? "disabled" : ""}
        >Weiter</button>
      </div>
    `;

    const previous = this.refs.pagination.querySelector(
      ".api-edit-grid-page-previous"
    );
    if (previous) {
      this.addEventListener(previous, "click", () => {
        if (this._currentPage > 1) {
          this._currentPage -= 1;
          this._actionRowIndex = null;
          this._editRowIndex = null;
          this.destroyRowEditor();
          this.renderUnifiedTable();
        }
      });
    }

    const next = this.refs.pagination.querySelector(
      ".api-edit-grid-page-next"
    );
    if (next) {
      this.addEventListener(next, "click", () => {
        if (this._currentPage < pageCount) {
          this._currentPage += 1;
          this._actionRowIndex = null;
          this._editRowIndex = null;
          this.destroyRowEditor();
          this.renderUnifiedTable();
        }
      });
    }
  }

  async openCrudEditor(rowIndex) {
    const isAdding = rowIndex === null;

    if (this.isActionOnly && !isAdding) {
      return;
    }

    if (isAdding && this.component.allowAdd === false) {
      return;
    }

    this.destroyRowEditor();
    this._isAdding = isAdding;
    this._editRowIndex = rowIndex;
    this._actionRowIndex = null;
    this.renderUnifiedTable();
  }

  async mountRowEditor() {
    if (!this.refs.tableBody || this._rowEditorForm) {
      return;
    }

    const container = this.refs.tableBody.querySelector(
      "[data-row-editor-container]"
    );
    if (!container) {
      return;
    }

    const row = this._isAdding
      ? {}
      : deepClone(this.dataValue?.[this._editRowIndex] || {});

    try {
      this._rowEditorForm = await Formio.createForm(
        container,
        {
          display: "form",
          components: this.buildRowEditorComponents()
        },
        {
          readOnly: false,
          noAlerts: true
        }
      );

      this._rowEditorForm.submission = {
        data: row
      };
    } catch (error) {
      this.setApiError(error);
    }
  }

  async saveCrudEditor() {
    if (!this._rowEditorForm || this._apiSaving) {
      return;
    }

    if (this.isActionOnly && !this._isAdding) {
      this.setApiError(
        new Error(
          "Vorhandene Datensätze können im Zeilenaktionsmodus nicht normal bearbeitet werden."
        )
      );
      return;
    }

    const valid =
      typeof this._rowEditorForm.checkValidity === "function"
        ? this._rowEditorForm.checkValidity(
            this._rowEditorForm.data,
            true
          )
        : true;

    if (!valid) {
      return;
    }

    const row = deepClone(
      this._rowEditorForm.submission?.data ||
      this._rowEditorForm.data ||
      {}
    );

    try {
      this._apiSaving = true;
      this.clearApiError();
      const savedRow = await this.persistRow(row);

      if (this._isAdding) {
        this.dataValue.push(deepClone(savedRow || row));
      } else if (this._editRowIndex !== null) {
        this.dataValue[this._editRowIndex] = deepClone(savedRow || row);
      }

      super.updateValue(this.dataValue, {
        modified: true,
        noUpdateEvent: false
      });

      this.closeCrudEditor(false);

      if (this.apiConfig.reloadAfterSave !== false) {
        this._apiLoadedSignature = null;
        await this.loadApiRows(true);
      } else {
        this.renderUnifiedTable();
      }
    } catch (error) {
      this.setApiError(error);
    } finally {
      this._apiSaving = false;
      this.redraw();
    }
  }

  closeCrudEditor(redraw = true) {
    this.destroyRowEditor();
    this._isAdding = false;
    this._editRowIndex = null;
    if (redraw) {
      this.renderUnifiedTable();
    }
  }

  destroyRowEditor() {
    if (
      this._rowEditorForm &&
      typeof this._rowEditorForm.destroy === "function"
    ) {
      try {
        this._rowEditorForm.destroy(true);
      } catch {
        // Ignorieren.
      }
    }
    this._rowEditorForm = null;
  }

  async deleteCrudRow(rowIndex) {
    const row = this.dataValue?.[rowIndex];
    if (!row || this.component.allowDelete === false) {
      return;
    }

    if (!window.confirm("Soll dieser Datensatz wirklich gelöscht werden?")) {
      return;
    }

    try {
      this._apiDeleting = true;
      this.clearApiError();

      if (this.isExistingRow(row)) {
        await this.deleteApiRow(row);
      }

      this.dataValue.splice(rowIndex, 1);
      super.updateValue(this.dataValue, {
        modified: true,
        noUpdateEvent: false
      });

      if (this.apiConfig.reloadAfterDelete !== false) {
        this._apiLoadedSignature = null;
        await this.loadApiRows(true);
      } else {
        this.renderUnifiedTable();
      }
    } catch (error) {
      this.setApiError(error);
    } finally {
      this._apiDeleting = false;
      this.redraw();
    }
  }

  getFilterStorageKey() {
    const formId =
      this.root?.component?.id ||
      this.root?.component?._id ||
      "form";
    return `apiEditGridFilter:${formId}:${this.component.key}`;
  }

  saveFilterState() {
    if (this.component.rememberFilters !== true) {
      return;
    }

    try {
      window.sessionStorage.setItem(
        this.getFilterStorageKey(),
        JSON.stringify(this._filterValues || {})
      );
    } catch {
      // Speicherfehler ignorieren.
    }
  }

  restoreFilterState() {
    if (this._filtersRestored || this.component.rememberFilters !== true) {
      return;
    }

    this._filtersRestored = true;
    try {
      const raw = window.sessionStorage.getItem(this.getFilterStorageKey());
      if (raw) {
        this._filterValues = JSON.parse(raw) || {};
      }
    } catch {
      this._filterValues = {};
    }
  }

  actionConditionMatches(row, condition) {
    if (!condition) {
      return true;
    }

    if (Array.isArray(condition.all)) {
      return condition.all.every((child) =>
        this.actionConditionMatches(row, child)
      );
    }

    if (Array.isArray(condition.any)) {
      return condition.any.some((child) =>
        this.actionConditionMatches(row, child)
      );
    }

    const field = String(condition.field || "").trim();
    if (!field) {
      return true;
    }

    const currentValue = getByPath(row, field);

    if (Object.prototype.hasOwnProperty.call(condition, "equals")) {
      return valuesMatch(
        currentValue,
        condition.equals,
        condition.ignoreCase !== false
      );
    }

    if (Object.prototype.hasOwnProperty.call(condition, "notEquals")) {
      return !valuesMatch(
        currentValue,
        condition.notEquals,
        condition.ignoreCase !== false
      );
    }

    if (Array.isArray(condition.in)) {
      return condition.in.some((expected) =>
        valuesMatch(currentValue, expected, condition.ignoreCase !== false)
      );
    }

    if (Array.isArray(condition.notIn)) {
      return !condition.notIn.some((expected) =>
        valuesMatch(currentValue, expected, condition.ignoreCase !== false)
      );
    }

    if (condition.hasValue === true) {
      return hasValue(currentValue);
    }

    if (condition.hasValue === false) {
      return !hasValue(currentValue);
    }

    return true;
  }

  buildVisibleWhenFromBuilder(action) {
    const conditions = normalizeArray(action.conditions)
      .filter((condition) =>
        condition && String(condition.field || "").trim() !== ""
      )
      .map((condition) => {
        const result = {
          field: String(condition.field).trim(),
          ignoreCase: condition.ignoreCase !== false
        };
        const operator = condition.operator || "equals";

        if (operator === "hasValue") {
          result.hasValue = true;
        } else if (operator === "hasNoValue") {
          result.hasValue = false;
        } else if (operator === "in" || operator === "notIn") {
          result[operator] = normalizeTextList(condition.value)
            .map(parseConfiguredValue);
        } else {
          result[operator] = parseConfiguredValue(condition.value);
        }

        // RowAction-Bedingungen werden gegen die geladene Zeile geprüft.
        // Die Zeile enthält weiterhin die originalen API-Felder und zusätzlich
        // die eindeutigen Form.io-Komponentenfelder aus fieldMappings.
        // Daher ist hier KEIN Field-Mapping erforderlich.
        return result;
      });

    if (conditions.length === 0) {
      return null;
    }

    return {
      [action.conditionMode === "any" ? "any" : "all"]: conditions
    };
  }

  hasPermissionAwareRowActions() {
    return normalizeArray(this.component.rowActions).some(
      (action) =>
        action &&
        String(action.requiredPermissionKey || "").trim() !== ""
    );
  }

  getEffectivePermissionCache() {
    return (
      window.__ozgEffectivePermissionsByKey &&
      typeof window.__ozgEffectivePermissionsByKey === "object"
        ? window.__ozgEffectivePermissionsByKey
        : null
    );
  }

  async ensureRowActionPermissionsLoaded() {
    if (!this.hasPermissionAwareRowActions()) {
      return;
    }

    if (this.getEffectivePermissionCache()) {
      return;
    }

    if (this._rowActionPermissionsLoading) {
      return;
    }

    this._rowActionPermissionsLoading = true;

    try {
      if (!window.__ozgEffectivePermissionsPromise) {
        window.__ozgEffectivePermissionsPromise = this.executeRequest(
          "GET",
          "/api/permission-management/effective-permissions"
        )
          .then((response) => {
            const permissions = normalizeArray(response?.data?.permissions);
            const map = {};

            permissions.forEach((permission) => {
              const key = String(permission?.permissionKey || "")
                .trim()
                .toLowerCase();

              if (!key) {
                return;
              }

              map[key] = Number(
                permission?.effectivePermissionLevel ?? 0
              );
            });

            window.__ozgEffectivePermissionsByKey = map;
            return map;
          })
          .catch((error) => {
            /*
             * Fail closed:
             * Eine konfigurierte RowAction-Berechtigung darf bei einem
             * Ladefehler nicht versehentlich als erlaubt behandelt werden.
             */
            console.warn(
              "[ApiEditGrid] Effektive Berechtigungen konnten nicht geladen werden.",
              error
            );

            window.__ozgEffectivePermissionsByKey = {};
            return window.__ozgEffectivePermissionsByKey;
          });
      }

      await window.__ozgEffectivePermissionsPromise;
    } finally {
      this._rowActionPermissionsLoading = false;

      if (this.refs && this.refs.tableBody) {
        this.renderUnifiedTable();
      }
    }
  }

  getRowActionPermissionLevel(action) {
    if (!action) {
      return 0;
    }

    const permissionKey = String(
      action.requiredPermissionKey || ""
    ).trim();

    /*
     * Bestehende RowActions ohne requiredPermissionKey bleiben
     * vollständig rückwärtskompatibel.
     */
    if (!permissionKey) {
      return 2;
    }

    const cache = this.getEffectivePermissionCache();

    /*
     * Bis die Berechtigungen geladen sind, wird die Action nicht
     * angeboten. ensureRowActionPermissionsLoaded() stößt das Laden an.
     */
    if (!cache) {
      return 0;
    }

    const normalizedKey = permissionKey.toLowerCase();

    return Number(cache[normalizedKey] ?? 0);
  }

  isRowActionDisabled(action) {
    return (
      action?.disabled === true ||
      this.getRowActionPermissionLevel(action) < 2
    );
  }

  isRowActionVisible(row, action) {
    if (!action) {
      return false;
    }

    if (this.getRowActionPermissionLevel(action) <= 0) {
      return false;
    }

    let visibleWhen = action.visibleWhen;

    if (
      typeof action.visibleWhenJson === "string" &&
      action.visibleWhenJson.trim() !== ""
    ) {
      try {
        visibleWhen = JSON.parse(action.visibleWhenJson);
      } catch (error) {
        console.warn("[ApiEditGrid] visibleWhenJson ist ungültig.", error);
      }
    } else if (!visibleWhen) {
      visibleWhen = this.buildVisibleWhenFromBuilder(action);
    }

    return this.actionConditionMatches(row, visibleWhen);
  }

  getNoActionMessage(row) {
    const status = row?.BookingStatusLabel || row?.bookingStatusLabel ||
      row?.StatusLabel || row?.statusLabel ||
      this.getStatusText(row?.BookingStatus || row?.bookingStatus || row?.Status || row?.status);
    const reason = row?.BookingBlockedReason || row?.bookingBlockedReason ||
      this.component.noActionMessage || "Für diesen Datensatz ist derzeit keine Aktion verfügbar.";
    return [status === "–" ? "" : status, reason].filter(Boolean).join(" – ");
  }

  renderRowDetails(row) {
    const configured = normalizeArray(this.component.detailFields);
    const fields = configured.length ? configured : this.getTableComponents();
    if (!fields.length) return "";
    return `<dl class="row api-edit-grid-row-details">${fields.map((field) => {
      const component = { ...field, key: field.field || field.key };
      if (!component.key) return "";
      return `<dt class="col-sm-4">${this.escapeHtml(field.label || component.key)}</dt>
        <dd class="col-sm-8">${this.escapeHtml(this.getDisplayCellValue(row, component))}</dd>`;
    }).join("")}</dl>`;
  }

  renderRowActions(row, rowIndex) {
    const actions = normalizeArray(this.component.rowActions).filter((action) =>
      this.isRowActionVisible(row, action)
    );

    return `
      ${this.renderRowDetails(row)}
      ${actions.length === 0 ? `<div class="alert alert-secondary mb-2">${this.escapeHtml(this.getNoActionMessage(row))}</div>` : ""}
      <div class="d-flex flex-wrap align-items-center">
        ${actions
          .map((action, actionIndex) => `
            <button
              type="button"
              class="btn btn-${this.escapeHtml(action.theme || "primary")} mr-2 mb-2 api-edit-grid-row-action"
              data-row-index="${rowIndex}"
              data-action-index="${actionIndex}"
              ${this.isRowActionDisabled(action) ? "disabled" : ""}
              aria-disabled="${this.isRowActionDisabled(action) ? "true" : "false"}"
            >
              ${this.escapeHtml(action.label || "Aktion ausführen")}
            </button>
          `)
          .join("")}
        <button
          type="button"
          class="btn btn-secondary mb-2 api-edit-grid-close-action-row"
        >Schließen</button>
      </div>
    `;
  }

  buildRowActionPayload(row, action) {
    if (action.sendRow === true) {
      return deepClone(row);
    }

    const payload = {};
    normalizeArray(action.payloadFields).forEach((field) => {
      const sourceField =
        typeof field === "string" ? field : field.sourceField;
      const targetField =
        typeof field === "string"
          ? field
          : field.targetField || field.sourceField;

      if (sourceField && targetField) {
        setByPath(
          payload,
          targetField,
          deepClone(getByPath(row, sourceField))
        );
      }
    });

    if (action.reason !== undefined) {
      setByPath(payload, action.reasonField || "reason", action.reason);
    }

    return payload;
  }

  async executeRowAction(rowIndex, action) {
    if (this._actionRunning || !action) {
      return;
    }

    if (this.getRowActionPermissionLevel(action) < 2) {
      console.warn(
        "[ApiEditGrid] RowAction wegen fehlender Bearbeitungsberechtigung blockiert:",
        action.requiredPermissionKey || action.key || action.label
      );
      return;
    }

    const row = this.dataValue?.[rowIndex];
    if (!row) {
      this.setApiError(new Error("Der ausgewählte Datensatz wurde nicht gefunden."));
      return;
    }

    if (!this.isRowActionVisible(row, action) || this.isRowActionDisabled(action)) {
      this.setApiError(new Error("Diese Aktion ist für den aktuellen Datensatz nicht verfügbar."));
      return;
    }

    if (action.confirmText && !window.confirm(action.confirmText)) {
      return;
    }

    let reason = action.reason || "";
    if (action.promptReason === true) {
      const enteredReason = window.prompt(
        action.reasonPrompt || "Bitte geben Sie optional einen Grund ein:",
        ""
      );
      if (enteredReason === null) {
        return;
      }
      reason = enteredReason;
    }

    const endpoint = resolveTemplate(action.endpoint, row);
    if (!endpoint) {
      this.setApiError(new Error("Für diese Aktion wurde kein Endpoint konfiguriert."));
      return;
    }

    const payload = this.buildRowActionPayload(row, {
      ...action,
      reason
    });

    try {
      this._actionRunning = true;
      this.clearApiError();
      const isFileDownload =
        action.downloadFile === true;

      const response = await this.executeRequest(
        action.method || "POST",
        endpoint,
        payload,
        isFileDownload
          ? { responseType: "blob" }
          : {}
      );

      if (isFileDownload) {
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], {
                type:
                  response?.headers?.["content-type"] ||
                  "application/octet-stream"
              });

        let fileName = "download";

        const contentDisposition =
          response?.headers?.["content-disposition"];

        if (contentDisposition) {
          const utf8Match =
            contentDisposition.match(
              /filename\*=UTF-8''([^;]+)/i
            );

          const normalMatch =
            contentDisposition.match(
              /filename="?([^";]+)"?/i
            );

          if (utf8Match && utf8Match[1]) {
            try {
              fileName =
                decodeURIComponent(utf8Match[1]);
            } catch (error) {
              fileName = utf8Match[1];
            }
          } else if (
            normalMatch &&
            normalMatch[1]
          ) {
            fileName = normalMatch[1];
          }
        }

        const downloadUrl =
          window.URL.createObjectURL(blob);

        const downloadLink =
          document.createElement("a");

        downloadLink.href = downloadUrl;
        downloadLink.download = fileName;

        document.body.appendChild(
          downloadLink
        );

        downloadLink.click();

        document.body.removeChild(
          downloadLink
        );

        window.setTimeout(() => {
          window.URL.revokeObjectURL(
            downloadUrl
          );
        }, 1000);
      }

      this._actionRowIndex = null;

      if (
        !isFileDownload &&
        action.reloadAfterAction !== false
      ) {
        this._apiLoadedSignature = null;
        await this.loadApiRows(true);
      } else {
        this.renderUnifiedTable();
      }

      if (!isFileDownload) {
        this.emitDataChanged(action.key, row);
      }
    } catch (error) {
      this.setApiError(error);
    } finally {
      this._actionRunning = false;
      this.redraw();
    }
  }

  async loadApiRows(
    force = false
  ) {
    if (
      this._apiLoading ||
      !this.shouldLoad()
    ) {
      return;
    }

    const signature =
      this.requestSignature;

    if (
      !force &&
      signature ===
        this._apiLoadedSignature
    ) {
      return;
    }

    if (
      this._loadingPromise
    ) {
      return this._loadingPromise;
    }

    this._apiLoading = true;
    this.clearApiError();
    this.redraw();

    const config =
      this.apiConfig;

    const endpoint =
      resolveTemplate(
        config.loadEndpoint,
        this.getFormData()
      );

    this._loadingPromise =
      this.executeRequest(
        config.loadMethod || "GET",
        endpoint
      )
        .then((response) => {
          const result =
            getByPath(
              response.data,
              config.resultPath
            );

          let sourceRows;

          if (
            this.component.singleObjectResponse === true &&
            result &&
            typeof result === "object" &&
            !Array.isArray(result)
          ) {
            sourceRows = [result];
          } else {
            sourceRows = normalizeArray(result);
          }

          const rows = sourceRows.map((row) =>
            mapApiRowToComponentRow(
              row,
              config
            )
          );

          this._loadedRows =
            deepClone(rows);

          this.setValue(
            deepClone(rows),
            {
              modified: false,
              noUpdateEvent: true,
              fromApi: true
            }
          );

          this._apiLoadedSignature =
  signature;

this._currentPage = 1;
this._actionRowIndex = null;
this._editRowIndex = null;

window.setTimeout(() => {
  this.refreshUnifiedControls();
  this.renderUnifiedTable();
}, 0);

return rows;
        })
        .catch((error) => {
          /*
           * Auch eine fehlgeschlagene Anfrage gehört zu dieser
           * Request-Signatur. Andernfalls lösen Redraw/attach/updateValue
           * denselben Request sofort wieder aus und erzeugen bei 401/403/500
           * eine Endlosschleife.
           *
           * Ein bewusster Reload mit force=true bleibt weiterhin möglich.
           */
          this._apiLoadedSignature =
            signature;

          this._loadedRows = [];

          this.setValue(
            [],
            {
              modified: false,
              noUpdateEvent: true,
              fromApi: true
            }
          );

          /*
           * Originalfehler übergeben, damit messageDe erhalten bleibt.
           */
          this.setApiError(error);

          return [];
        })
        .finally(() => {
          this._apiLoading = false;
          this._loadingPromise = null;

          this.refreshUnifiedControls();
          this.renderUnifiedTable();
        });

        return this._loadingPromise;
      }

  /**
   * Liefert true, wenn die Zeile bereits
   * eine Datenbank-ID besitzt.
   */
  isExistingRow(row) {
    const id =
      getByPath(
        row,
        this.idField
      );

    return (
      id !== null &&
      id !== undefined &&
      id !== "" &&
      Number(id) > 0
    );
  }

  /**
   * Wird von Form.io beim Speichern
   * einer EditGrid-Zeile aufgerufen.
   */
  async saveRow(
    rowIndex,
    modified
  ) {

    if (this.isActionOnly) {
  this._actionRowIndex = null;
  this.renderUnifiedTable();
  return true;
}

    const row =
      this.dataValue?.[rowIndex];

    if (!row) {
      return super.saveRow(
        rowIndex,
        modified
      );
    }

    const wasExisting =
      this.isExistingRow(row);

    if (
      !this.component.allowEdit &&
      wasExisting
    ) {
      this.setApiError(
        new Error(
          "Bestehende Datensätze dürfen nicht bearbeitet werden."
        )
      );

      return false;
    }

    if (
      !this.component.allowAdd &&
      !wasExisting
    ) {
      this.setApiError(
        new Error(
          "Neue Datensätze dürfen nicht angelegt werden."
        )
      );

      return false;
    }

    const validationResult =
      await this.validateRowBeforeSave(
        rowIndex
      );

    if (!validationResult) {
      return false;
    }

    try {
      this._apiSaving = true;
      this.clearApiError();
      this.redraw();

      const savedRow =
        await this.persistRow(row);

      if (savedRow) {
        this.dataValue[rowIndex] =
          deepClone(savedRow);
      }

      const result =
        await super.saveRow(
          rowIndex,
          modified
        );

      if (
        this.apiConfig
          .reloadAfterSave !== false
      ) {
        await this.loadApiRows(true);
      } else {
        this.updateValue(
          this.dataValue,
          {
            modified: false,
            noUpdateEvent: true
          }
        );
      }

      this.emitDataChanged(
        wasExisting ? "crud_update" : "crud_create",
        savedRow || row
      );

      return result;
    } catch (error) {
      this.setApiError(error);
      return false;
    } finally {
      this._apiSaving = false;
      this.redraw();
    }
  }

  async validateRowBeforeSave(
    rowIndex
  ) {
    try {
      const editRow =
        this.editRows?.[rowIndex];

      if (
        editRow &&
        Array.isArray(
          editRow.components
        )
      ) {
        let valid = true;

        editRow.components.forEach(
          (component) => {
            if (
              component &&
              typeof component.checkValidity ===
                "function"
            ) {
              const componentValid =
                component.checkValidity(
                  component.data,
                  true
                );

              if (!componentValid) {
                valid = false;
              }
            }
          }
        );

        return valid;
      }

      return true;
    } catch (error) {
      console.warn(
        "[ApiEditGrid] Validierung fehlgeschlagen",
        error
      );

      return false;
    }
  }

  async persistRow(row) {
    const config =
      this.apiConfig;

    const existing =
      !this._isAdding &&
      this.isExistingRow(row);

    if (this.isActionOnly && existing) {
      throw new Error(
        "Vorhandene Datensätze können im Zeilenaktionsmodus nicht normal bearbeitet werden."
      );
    }

    const endpointTemplate =
      existing
        ? config.updateEndpoint
        : config.createEndpoint;

    const method =
      existing
        ? config.updateMethod || "PUT"
        : config.createMethod || "POST";

    if (!endpointTemplate) {
      throw new Error(
        existing
          ? "Es wurde kein Update-Endpoint konfiguriert."
          : "Es wurde kein Create-Endpoint konfiguriert."
      );
    }

    const endpoint =
      resolveTemplate(
        endpointTemplate,
        {
          ...(this.getFormData() || {}),
          ...(row || {})
        }
      );

    const payloadPath =
      existing
        ? config.updatePayloadPath
        : config.createPayloadPath;

    const payload =
      this.buildPayload(
        row,
        payloadPath,
        true
      );

    const response =
      await this.executeRequest(
        method,
        endpoint,
        payload
      );

    const responsePath =
      existing
        ? config.updateResultPath
        : config.createResultPath;

    if (responsePath) {
      const result =
        getByPath(
          response.data,
          responsePath
        );

      if (result) {
        return result;
      }
    }

    if (
      response.data &&
      typeof response.data === "object"
    ) {
      if (response.data.item) {
        return mapApiRowToComponentRow(
          response.data.item,
          config
        );
      }

      if (response.data.data) {
        return mapApiRowToComponentRow(
          response.data.data,
          config
        );
      }

      if (
        !Array.isArray(
          response.data
        )
      ) {
        return mapApiRowToComponentRow(
          response.data,
          config
        );
      }
    }

    return mapApiRowToComponentRow(
      row,
      config
    );
  }

  /**
   * Wird von Form.io beim Entfernen einer
   * EditGrid-Zeile aufgerufen.
   */
  async removeRow(
    rowIndex
  ) {

    if (this.isActionOnly) {
  this._actionRowIndex = null;
  this.renderUnifiedTable();
  return true;
}

    const row =
      this.dataValue?.[rowIndex];

    if (!row) {
      return super.removeRow(
        rowIndex
      );
    }

    const existing =
      this.isExistingRow(row);

    if (
      existing &&
      !this.component.allowDelete
    ) {
      this.setApiError(
        new Error(
          "Bestehende Datensätze dürfen nicht gelöscht werden."
        )
      );

      return false;
    }

    if (!existing) {
      return super.removeRow(
        rowIndex
      );
    }

    const confirmed =
      window.confirm(
        "Soll dieser Datensatz wirklich gelöscht werden?"
      );

    if (!confirmed) {
      return false;
    }

    try {
      this._apiDeleting = true;
      this.clearApiError();
      this.redraw();

      await this.deleteApiRow(row);

      const result =
        await super.removeRow(
          rowIndex
        );

      if (
        this.apiConfig
          .reloadAfterDelete !== false
      ) {
        await this.loadApiRows(true);
      }

      this.emitDataChanged("crud_delete", row);

      return result;
    } catch (error) {
      this.setApiError(error);
      return false;
    } finally {
      this._apiDeleting = false;
      this.redraw();
    }
  }

  async deleteApiRow(row) {
    const config =
      this.apiConfig;

    if (!config.deleteEndpoint) {
      throw new Error(
        "Es wurde kein Delete-Endpoint konfiguriert."
      );
    }

    const endpoint =
      resolveTemplate(
        config.deleteEndpoint,
        {
          ...(this.getFormData() || {}),
          ...(row || {})
        }
      );

    const payload =
      this.buildPayload(
        row,
        config.deletePayloadPath
      );

    return this.executeRequest(
      config.deleteMethod ||
        "DELETE",
      endpoint,
      payload
    );
  }

  /**
   * Verhindert, dass Form.io später
   * alte formValue-Daten über die
   * API-Daten schreibt.
   */
  setValue(value, flags = {}) {
    if (
      flags.fromApi === true
    ) {
      return super.setValue(
        normalizeArray(value),
        flags
      );
    }

    if (
      this._apiLoadedSignature &&
      flags.fromSubmission === true
    ) {
      return false;
    }

    return super.setValue(
      value,
      flags
    );
  }

  updateValue(
    value,
    flags = {}
  ) {
    const changed =
      super.updateValue(
        value,
        flags
      );

    // Im Form.io Builder keine API-Reloads auslösen.
    if (this.isBuilderMode) {
      return changed;
    }

    const signature =
      this.requestSignature;

    if (
      signature !==
        this._apiLoadedSignature &&
      !this._apiLoading &&
      !this._apiSaving &&
      !this._apiDeleting
    ) {
      setTimeout(() => {
        this.loadApiRows(true);
      }, 0);
    }

    return changed;
  }

  getValue() {
    return normalizeArray(
      super.getValue()
    );
  }

  destroy() {
    this._loadingPromise = null;
    this.destroyRowEditor();

    if (
      this._dataChangedHandler &&
      this.root &&
      typeof this.root.off === "function"
    ) {
      this.root.off("ozgApiDataChanged", this._dataChangedHandler);
    }

    if (
      this._rootDependencyChangeHandler &&
      this.root &&
      typeof this.root.off === "function"
    ) {
      this.root.off("change", this._rootDependencyChangeHandler);
    }

    window.clearTimeout(this._rootDependencyChangeTimer);

    this._dataChangedHandler = null;
    this._rootDependencyChangeHandler = null;
    this._rootDependencyChangeTimer = null;

    return super.destroy();
  }
}

/**
 * Konfiguration im Form.io-Builder.
 */
ApiEditGridComponent.editForm =
  function () {
    const crudOnly = {
      json: {
        "!==": [
          {
            var: "data.actionOnly"
          },
          true
        ]
      }
    };

    const actionsOnly = {
      json: {
        "===": [
          {
            var: "data.actionOnly"
          },
          true
        ]
      }
    };

    return {
      components: [
        {
          type: "tabs",
          key: "tabs",
          components: [
            {
              key: "display",
              label: "Anzeige",
              components: [
                {
                  type: "textfield",
                  key: "label",
                  label: "Label",
                  description:
                    "Bezeichnung der Komponente im Formular."
                },
                {
                  type: "textfield",
                  key: "key",
                  label: "API-Key",
                  validate: {
                    required: true
                  },
                  description:
                    "Eindeutiger technischer Schlüssel der Komponente."
                },
                {
                  type: "checkbox",
                  key: "persistent",
                  label:
                    "In Formulardaten speichern",
                  defaultValue: false,
                  description:
                    "Speichert die geladenen beziehungsweise bearbeiteten Zeilen in der Form.io-Submission."
                },
                {
                  type: "checkbox",
                  key: "displayAsTable",
                  label:
                    "Als Tabelle anzeigen",
                  defaultValue: true,
                  description:
                    "Verwendet die tabellarische Darstellung des EditGrids."
                },
                {
                  type: "select",
                  key: "editorColumnCount",
                  label: "Spalten im Bearbeitungseditor",
                  data: {
                    values: [
                      { label: "1 Spalte", value: 1 },
                      { label: "2 Spalten", value: 2 }
                    ]
                  },
                  defaultValue: 1,
                  description:
                    "Beeinflusst nur den Hinzufügen-/Bearbeiten-Editor; die Tabellenansicht bleibt unverändert."
                },
                {
                  type: "checkbox",
                  key: "reloadOnDataChanged",
                  label: "Nach Datenänderungen anderer API-Komponenten neu laden",
                  defaultValue: false,
                  description:
                    "Lädt die API-Daten neu, wenn eine andere API-Komponente das neutrale Datenänderungsereignis auslöst."
                },
                {
                  type: "checkbox",
                  key: "actionOnly",
                  label:
                    "Zeilenaktionen für vorhandene Datensätze verwenden",
                  defaultValue: false,
                  description:
                    "Vorhandene Datensätze werden nicht über die normalen Bearbeiten- und Löschen-Funktionen verwaltet. Der Stift öffnet stattdessen die unter „Zeilenaktionen“ konfigurierten API-Aktionen. Neue Datensätze können weiterhin über „Hinzufügen“ angelegt, gespeichert und abgebrochen werden. Der Create-Endpunkt bleibt aktiv; Update und Delete werden für vorhandene Zeilen ersetzt."
                },
                {
                  type: "textfield",
                  key: "addAnother",
                  label:
                    "Text: Hinzufügen",
                  defaultValue:
                    "Hinzufügen"
                },
                {
                  type: "textfield",
                  key: "saveRow",
                  label:
                    "Text: Übernehmen",
                  defaultValue:
                    "Übernehmen"
                },
                {
                  type: "textfield",
                  key: "removeRow",
                  label:
                    "Text: Abbrechen",
                  defaultValue:
                    "Abbrechen"
                },
                {
                  type: "checkbox",
                  key: "allowAdd",
                  label:
                    "Neue Datensätze erlauben",
                  defaultValue: true,
                  description:
                    "Blendet die Schaltfläche zum Anlegen neuer Zeilen ein. Diese Funktion bleibt auch bei aktivierten Zeilenaktionen verfügbar."
                },
                {
                  type: "checkbox",
                  key: "allowEdit",
                  label:
                    "Bearbeitung erlauben",
                  defaultValue: true,
                  conditional: crudOnly,
                  description:
                    "Erlaubt die Bearbeitung vorhandener API-Datensätze."
                },
                {
                  type: "checkbox",
                  key: "allowDelete",
                  label:
                    "Löschen erlauben",
                  defaultValue: true,
                  conditional: crudOnly,
                  description:
                    "Blendet die Löschaktion für vorhandene Zeilen ein."
                }
              ]
            },
            {
              key: "apiLoad",
              label: "API: Laden",
              components: [
                {
                  type: "textfield",
                  key:
                    "api.loadEndpoint",
                  label:
                    "Lade-Endpoint",
                  placeholder:
                    "/api/permission-management/groups",
                  validate: {
                    required: true
                  },
                  description:
                    "Relativer API-Pfad zum Laden der Tabellenzeilen. Platzhalter können Werte aus dem Form.io-Datenmodell enthalten, beispielsweise /api/groups/{groupId}."
                },
                {
                  type: "select",
                  key:
                    "api.loadMethod",
                  label:
                    "Lademethode",
                  data: {
                    values: [
                      {
                        label: "GET",
                        value: "GET"
                      },
                      {
                        label: "POST",
                        value: "POST"
                      }
                    ]
                  },
                  defaultValue: "GET",
                  description:
                    "HTTP-Methode des Ladeaufrufs."
                },
                {
                  type: "textfield",
                  key:
                    "api.resultPath",
                  label:
                    "Pfad zum Ergebnis-Array",
                  placeholder:
                    "groups",
                  description:
                    "Punktpfad zum Ergebnis innerhalb der API-Antwort. Leer lassen, wenn die API das Ergebnis direkt zurückgibt."
                },
                {
                  type: "checkbox",
                  key: "singleObjectResponse",
                  label: "API liefert einen einzelnen Datensatz",
                  defaultValue: false,
                  description:
                    "Aktivieren, wenn der Lade-Endpunkt ein einzelnes JSON-Objekt statt eines Arrays zurückgibt."
                },
                {
                  type: "textfield",
                  key:
                    "api.idField",
                  label:
                    "ID-Feld",
                  defaultValue: "id",
                  description:
                    "Eindeutiges Feld eines vorhandenen Datensatzes. Es wird für Update, Delete und Platzhalter in Zeilenaktionen verwendet."
                },
                {
                  type: "textfield",
                  key:
                    "api.dependsOnText",
                  label:
                    "Abhängige Form.io-Felder",
                  placeholder:
                    "selectedGroupId, scopeType",
                  description:
                    "Mehrere Feldschlüssel durch Komma trennen. Die API wird erst geladen, wenn alle Felder einen Wert besitzen. Änderungen lösen ein erneutes Laden aus."
                },
                {
                  type: "checkbox",
                  key:
                    "api.autoLoad",
                  label:
                    "Automatisch laden",
                  defaultValue: true,
                  description:
                    "Lädt die API-Daten automatisch, sobald die Komponente und alle Abhängigkeiten verfügbar sind."
                }
              ]
            },
            {
              key: "apiEdit",
              label:
                "API: Bearbeiten",
              components: [
                {
                  type: "panel",
                  title: "Anlegen",
                  key: "createPanel",
                  components: [
                    {
                      type: "textfield",
                      key:
                        "api.createEndpoint",
                      label:
                        "Create-Endpoint",
                      placeholder:
                        "/api/permission-management/groups",
                      description:
                        "Endpoint zum Anlegen einer neuen Zeile."
                    },
                    {
                      type: "select",
                      key:
                        "api.createMethod",
                      label:
                        "Create-Methode",
                      data: {
                        values: [
                          {
                            label: "POST",
                            value: "POST"
                          },
                          {
                            label: "PUT",
                            value: "PUT"
                          }
                        ]
                      },
                      defaultValue: "POST"
                    },
                    {
                      type: "textfield",
                      key:
                        "api.createPayloadPath",
                      label:
                        "Payload-Pfad",
                      placeholder:
                        "group",
                      description:
                        "Optionaler Zielpfad für die neue Zeile im Request-Body. Leer bedeutet: Zeilenfelder direkt im Root-Objekt senden."
                    },
                    {
                      type: "textfield",
                      key:
                        "api.createResultPath",
                      label:
                        "Pfad zum gespeicherten Datensatz",
                      placeholder:
                        "group",
                      description:
                        "Optionaler Punktpfad zum angelegten Datensatz in der API-Antwort."
                    }
                  ]
                },
                {
                  type: "panel",
                  title: "Ändern",
                  key: "updatePanel",
                  conditional: crudOnly,
                  components: [
                    {
                      type: "textfield",
                      key:
                        "api.updateEndpoint",
                      label:
                        "Update-Endpoint",
                      placeholder:
                        "/api/permission-management/groups/{groupId}",
                      description:
                        "Endpoint zum Aktualisieren eines vorhandenen Datensatzes. Platzhalter werden aus der aktuellen Zeile ersetzt."
                    },
                    {
                      type: "select",
                      key:
                        "api.updateMethod",
                      label:
                        "Update-Methode",
                      data: {
                        values: [
                          {
                            label: "PUT",
                            value: "PUT"
                          },
                          {
                            label: "PATCH",
                            value: "PATCH"
                          },
                          {
                            label: "POST",
                            value: "POST"
                          }
                        ]
                      },
                      defaultValue: "PUT"
                    },
                    {
                      type: "textfield",
                      key:
                        "api.updatePayloadPath",
                      label:
                        "Payload-Pfad",
                      placeholder:
                        "group"
                    },
                    {
                      type: "textfield",
                      key:
                        "api.updateResultPath",
                      label:
                        "Pfad zum gespeicherten Datensatz",
                      placeholder:
                        "group"
                    }
                  ]
                },
                {
                  type: "panel",
                  title: "Löschen",
                  key: "deletePanel",
                  conditional: crudOnly,
                  components: [
                    {
                      type: "textfield",
                      key:
                        "api.deleteEndpoint",
                      label:
                        "Delete-Endpoint",
                      placeholder:
                        "/api/permission-management/groups/{groupId}",
                      description:
                        "Endpoint zum Löschen eines vorhandenen Datensatzes. Platzhalter werden aus der aktuellen Zeile ersetzt."
                    },
                    {
                      type: "select",
                      key:
                        "api.deleteMethod",
                      label:
                        "Delete-Methode",
                      data: {
                        values: [
                          {
                            label: "DELETE",
                            value: "DELETE"
                          },
                          {
                            label: "POST",
                            value: "POST"
                          }
                        ]
                      },
                      defaultValue:
                        "DELETE"
                    },
                    {
                      type: "textfield",
                      key:
                        "api.deletePayloadPath",
                      label:
                        "Payload-Pfad",
                      placeholder:
                        "group"
                    }
                  ]
                },
                {
                  type: "textfield",
                  key:
                    "api.contextFieldsText",
                  label:
                    "Zusätzliche Formularfelder im Payload",
                  placeholder:
                    "communityId, snapshotVersion",
                  description:
                    "Mehrere Feldschlüssel durch Komma trennen. Optionales Mapping mit Quelle:Ziel, z. B. SelectedCommunityId:CommunityId. Ohne Mapping bleibt der Feldname unverändert."
                },
                {
                  type: "checkbox",
                  key:
                    "api.reloadAfterSave",
                  label:
                    "Nach Speichern neu laden",
                  defaultValue: true
                },
                {
                  type: "checkbox",
                  key:
                    "api.reloadAfterDelete",
                  label:
                    "Nach Löschen neu laden",
                  defaultValue: true,
                  conditional: crudOnly
                }
              ]
            },
            {
              key: "actionTable",
              label: "Tabellenansicht",
              components: [
                {
                  type: "checkbox",
                  key: "searchEnabled",
                  label: "Suchfeld anzeigen",
                  defaultValue: false,
                  description:
                    "Zeigt oberhalb der Tabelle ein Suchfeld an. Durchsucht werden alle als Tabellenspalte sichtbaren Zeilenfelder."
                },
                {
                  type: "textfield",
                  key: "searchPlaceholder",
                  label:
                    "Platzhalter des Suchfelds",
                  defaultValue:
                    "Datensätze durchsuchen",
                  description:
                    "Text, der im leeren Suchfeld angezeigt wird.",
                  conditional: {
                    json: {
                      "===": [
                        {
                          var:
                            "data.searchEnabled"
                        },
                        true
                      ]
                    }
                  }
                },
                {
                  type: "textfield",
                  key: "searchFieldsText",
                  label:
                    "Durchsuchte Zeilenfelder",
                  placeholder:
                    "name, description, status",
                  description:
                    "Kommagetrennte Liste technischer Feldschlüssel. Leer bedeutet: alle sichtbaren Tabellenspalten durchsuchen.",
                  conditional: {
                    json: {
                      "===": [
                        {
                          var:
                            "data.searchEnabled"
                        },
                        true
                      ]
                    }
                  }
                },
                {
                  type: "checkbox",
                  key: "paginationEnabled",
                  label: "Seitennavigation aktivieren",
                  defaultValue: true,
                  description:
                    "Aktiviert die Seitennavigation für normale CRUD-Tabellen und Action-Only-Tabellen."
                },
                {
                  type: "number",
                  key: "pageSize",
                  label:
                    "Einträge pro Seite",
                  defaultValue: 10,
                  validate: {
                    required: true,
                    min: 1,
                    integer: true
                  },
                  description:
                    "Maximale Anzahl gleichzeitig dargestellter Tabellenzeilen."
                },
                {
                  type: "textfield",
                  key: "pageSizeOptionsText",
                  label:
                    "Auswählbare Seitengrößen",
                  defaultValue:
                    "5, 10, 25, 50",
                  description:
                    "Kommagetrennte Liste der Seitengrößen. Bei mehreren Werten wird in der Tabelle eine Auswahl angezeigt."
                },
                {
                  type: "checkbox",
                  key: "sortingEnabled",
                  label:
                    "Sortierung über Spaltenüberschriften aktivieren",
                  defaultValue: true,
                  description:
                    "Ermöglicht die auf- und absteigende Sortierung durch Klick auf eine Tabellenüberschrift."
                },
                {
                  type: "textfield",
                  key: "defaultSortField",
                  label:
                    "Standard-Sortierfeld",
                  placeholder:
                    "name",
                  description:
                    "Technischer Schlüssel eines sichtbaren Zeilenfeldes. Leer bedeutet: keine anfängliche Sortierung."
                },
                {
                  type: "select",
                  key: "defaultSortDirection",
                  label:
                    "Standard-Sortierrichtung",
                  data: {
                    values: [
                      {
                        label:
                          "Aufsteigend",
                        value: "asc"
                      },
                      {
                        label:
                          "Absteigend",
                        value: "desc"
                      }
                    ]
                  },
                  defaultValue: "asc"
                },
                {
                  type: "textfield",
                  key: "actionColumnLabel",
                  label:
                    "Bezeichnung der Aktionsspalte",
                  defaultValue:
                    "Aktion",
                  description:
                    "Überschrift der rechten Tabellenspalte mit dem Stift-Symbol."
                },
                {
                  type: "textfield",
                  key: "actionOpenLabel",
                  label:
                    "Tooltip des Aktionsbuttons",
                  defaultValue:
                    "Aktionen öffnen",
                  description:
                    "Beschriftung und Tooltip des Stift-Symbols."
                },
                {
                  type: "textfield",
                  key: "emptyMessage",
                  label:
                    "Text bei leerer Tabelle",
                  defaultValue:
                    "Keine Datensätze vorhanden.",
                  description:
                    "Meldung, wenn die API keine Datensätze liefert."
                },
                {
                  type: "textfield",
                  key: "noFilterResultMessage",
                  label:
                    "Text ohne Such- oder Filtertreffer",
                  defaultValue:
                    "Keine passenden Datensätze gefunden."
                },
                {
                  type: "textfield",
                  key: "loadingMessage",
                  label:
                    "Text beim Laden",
                  defaultValue:
                    "Daten werden geladen …"
                },
                {
                  type: "textfield",
                  key: "savingMessage",
                  label:
                    "Text beim Speichern",
                  defaultValue:
                    "Datensatz wird gespeichert …"
                },
                {
                  type: "textfield",
                  key: "deletingMessage",
                  label:
                    "Text beim Löschen",
                  defaultValue:
                    "Datensatz wird gelöscht …"
                },
                {
                  type: "textfield",
                  key: "actionRunningMessage",
                  label:
                    "Text während einer Zeilenaktion",
                  defaultValue:
                    "Aktion wird ausgeführt …"
                },
                {
                  type: "textfield",
                  key: "loadErrorMessage",
                  label:
                    "Allgemeiner Ladefehlertext",
                  defaultValue:
                    "Die Datensätze konnten nicht geladen werden."
                },
                {
                  type: "textfield",
                  key: "noActionMessage",
                  label:
                    "Text ohne verfügbare Aktion",
                  defaultValue:
                    "Für diesen Datensatz ist derzeit keine Aktion verfügbar.",
                  description:
                    "Meldung, wenn keine Zeilenaktion ihre Sichtbarkeitsbedingungen erfüllt."
                }
              ]
            },
            {
              key: "filtersTab",
              label: "Filter",
              components: [
                {
                  type: "checkbox",
                  key: "filtersEnabled",
                  label: "Filter anzeigen",
                  defaultValue: false,
                  description:
                    "Zeigt oberhalb der Tabelle konfigurierbare Benutzerfilter an. Die Filter funktionieren sowohl im CRUD- als auch im Action-Only-Modus."
                },
                {
                  type: "checkbox",
                  key: "filtersCollapsible",
                  label: "Filterbereich ein- und ausklappbar",
                  defaultValue: true,
                  description:
                    "Ermöglicht dem Benutzer, den Filterbereich platzsparend ein- und auszuklappen."
                },
                {
                  type: "checkbox",
                  key: "filtersInitiallyOpen",
                  label: "Filterbereich standardmäßig geöffnet",
                  defaultValue: false
                },
                {
                  type: "checkbox",
                  key: "showResetFiltersButton",
                  label: "Schaltfläche „Filter zurücksetzen“ anzeigen",
                  defaultValue: true
                },
                {
                  type: "checkbox",
                  key: "rememberFilters",
                  label: "Filter während der Sitzung merken",
                  defaultValue: false,
                  description:
                    "Speichert die Filterwerte im sessionStorage und stellt sie beim erneuten Öffnen der Seite wieder her."
                },
                {
                  type: "editgrid",
                  key: "filterDefinitions",
                  label: "Filterdefinitionen",
                  addAnother: "Filter hinzufügen",
                  saveRow: "Filter übernehmen",
                  removeRow: "Abbrechen",
                  description:
                    "Definiert die Felder, nach denen Benutzer die Tabellenzeilen filtern können.",
                  components: [
                    {
                      type: "textfield",
                      key: "key",
                      label: "Technischer Filterschlüssel",
                      placeholder: "statusFilter",
                      validate: { required: true },
                      description:
                        "Eindeutiger Schlüssel des Filterzustands innerhalb dieser Komponente."
                    },
                    {
                      type: "textfield",
                      key: "field",
                      label: "Datenfeld",
                      placeholder: "Status",
                      validate: { required: true },
                      description:
                        "Punktpfad zum Feld innerhalb der API-Zeile, zum Beispiel Status oder details.name."
                    },
                    {
                      type: "textfield",
                      key: "label",
                      label: "Bezeichnung",
                      placeholder: "Status",
                      validate: { required: true }
                    },
                    {
                      type: "select",
                      key: "type",
                      label: "Filtertyp",
                      data: {
                        values: [
                          { label: "Text", value: "text" },
                          { label: "Auswahlliste", value: "select" },
                          { label: "Mehrfachauswahl", value: "multiselect" },
                          { label: "Ja/Nein", value: "boolean" },
                          { label: "Zahl", value: "number" },
                          { label: "Zahlenbereich", value: "numberRange" },
                          { label: "Datum", value: "date" },
                          { label: "Datumsbereich", value: "dateRange" }
                        ]
                      },
                      defaultValue: "text",
                      validate: { required: true }
                    },
                    {
                      type: "select",
                      key: "operator",
                      label: "Textvergleich",
                      data: {
                        values: [
                          { label: "Enthält", value: "contains" },
                          { label: "Beginnt mit", value: "startsWith" },
                          { label: "Endet mit", value: "endsWith" },
                          { label: "Ist gleich", value: "equals" },
                          { label: "Ist nicht gleich", value: "notEquals" }
                        ]
                      },
                      defaultValue: "contains",
                      description:
                        "Wird nur bei Textfiltern ausgewertet."
                    },
                    {
                      type: "textfield",
                      key: "placeholder",
                      label: "Platzhalter",
                      description:
                        "Optionaler Hinweistext innerhalb eines Text-, Zahlen- oder Datumsfilters."
                    },
                    {
                      type: "textfield",
                      key: "valuesText",
                      label: "Feste Auswahlwerte",
                      placeholder: "active, scheduled, expired",
                      description:
                        "Kommagetrennte Werte für Select- und Mehrfachauswahlfilter. Leer lassen, um die Werte automatisch aus den geladenen Tabellenzeilen zu ermitteln."
                    },
                    {
                      type: "checkbox",
                      key: "ignoreCase",
                      label: "Groß-/Kleinschreibung ignorieren",
                      defaultValue: true
                    }
                  ]
                }
              ]
            },
            {
              key: "rowActionsTab",
              label: "Zeilenaktionen",
              conditional: actionsOnly,
              components: [
                {
                  type: "checkbox",
                  key: "showRowDetails",
                  label: "Details auch bei direkter Einzelaktion öffnen",
                  defaultValue: false,
                  description: "Der Detailknopf bleibt auch ohne verfügbare Vertragsaktion sichtbar."
                },
                {
                  type: "editgrid",
                  key: "detailFields",
                  label: "Felder im Detailbereich",
                  description: "Ohne Einträge werden die vorhandenen Tabellenspalten angezeigt. Feldnamen müssen zur geladenen API-Zeile passen.",
                  components: [
                    { type: "textfield", key: "field", label: "Feldpfad", input: true },
                    { type: "textfield", key: "label", label: "Beschriftung", input: true }
                  ]
                },
                {
                  type: "editgrid",
                  key: "rowActions",
                  label: "Zeilenaktionen",
                  addAnother:
                    "Aktion hinzufügen",
                  saveRow:
                    "Aktion übernehmen",
                  removeRow:
                    "Abbrechen",
                  description:
                    "Definiert die API-Aktionen, die nach dem Öffnen einer Tabellenzeile angeboten werden.",
                  components: [
                    {
                      type: "textfield",
                      key: "label",
                      label: "Bezeichnung",
                      validate: {
                        required: true
                      },
                      description:
                        "Text des Aktionsbuttons."
                    },
                    {
                      type: "textfield",
                      key: "key",
                      label:
                        "Technischer Schlüssel",
                      validate: {
                        required: true
                      },
                      description:
                        "Eindeutiger technischer Schlüssel, beispielsweise cancelRecord."
                    },
                    {
                      type: "textfield",
                      key: "requiredPermissionKey",
                      label: "Erforderlicher Permission-Key",
                      placeholder:
                        "systemeinstellungen.formulareVerwalten.formularVerwaltungGemeindePanel.bookForm",
                      description:
                        "Optional. Berechtigungsstufe 0 blendet die Aktion aus, Stufe 1 zeigt sie deaktiviert, Stufe 2 erlaubt die Ausführung."
                    },
                    {
                      type: "select",
                      key: "theme",
                      label:
                        "Button-Darstellung",
                      data: {
                        values: [
                          {
                            label: "Primär",
                            value: "primary"
                          },
                          {
                            label: "Sekundär",
                            value: "secondary"
                          },
                          {
                            label: "Erfolg",
                            value: "success"
                          },
                          {
                            label: "Warnung",
                            value: "warning"
                          },
                          {
                            label: "Gefahr",
                            value: "danger"
                          },
                          {
                            label: "Information",
                            value: "info"
                          },
                          {
                            label: "Hell",
                            value: "light"
                          }
                        ]
                      },
                      defaultValue:
                        "primary",
                      description:
                        "Bootstrap-Darstellung des Buttons."
                    },
                    {
                      type: "select",
                      key: "method",
                      label: "HTTP-Methode",
                      data: {
                        values: [
                          {
                            label: "POST",
                            value: "POST"
                          },
                          {
                            label: "PUT",
                            value: "PUT"
                          },
                          {
                            label: "PATCH",
                            value: "PATCH"
                          },
                          {
                            label: "DELETE",
                            value: "DELETE"
                          },
                          {
                            label: "GET",
                            value: "GET"
                          }
                        ]
                      },
                      defaultValue:
                        "POST"
                    },
                    {
                      type: "textfield",
                      key: "endpoint",
                      label: "Endpoint",
                      placeholder:
                        "/api/items/{id}/action",
                      validate: {
                        required: true
                      },
                      description:
                        "Relativer API-Pfad. Platzhalter in geschweiften Klammern werden aus der aktuellen Tabellenzeile ersetzt."
                    },
                    {
                      type: "checkbox",
                      key: "downloadFile",
                      label:
                        "Antwort als Datei herunterladen",
                      defaultValue: false,
                      description:
                        "Behandelt die API-Antwort als Datei-Download. Dateiname und Dateityp werden nach Möglichkeit aus den HTTP-Response-Headern übernommen."
                    },
                    {
                      type: "checkbox",
                      key: "sendRow",
                      label:
                        "Gesamte Zeile senden",
                      defaultValue: false,
                      description:
                        "Sendet den vollständigen Datensatz als Request-Body. Sonst werden nur Payload-Felder und feste Werte übertragen."
                    },
                    {
                      type: "editgrid",
                      key: "payloadFields",
                      label:
                        "Payload-Felder",
                      addAnother:
                        "Feldzuordnung hinzufügen",
                      saveRow:
                        "Zuordnung übernehmen",
                      removeRow:
                        "Abbrechen",
                      description:
                        "Übernimmt ausgewählte Werte aus der Tabellenzeile in den Request-Body.",
                      components: [
                        {
                          type: "textfield",
                          key: "sourceField",
                          label: "Quellfeld",
                          placeholder:
                            "RowVersion",
                          validate: {
                            required: true
                          },
                          description:
                            "Punktpfad zum Wert in der aktuellen Tabellenzeile."
                        },
                        {
                          type: "textfield",
                          key: "targetField",
                          label:
                            "Zielfeld im Request",
                          placeholder:
                            "rowVersion",
                          validate: {
                            required: true
                          },
                          description:
                            "Punktpfad, unter dem der Wert im Request-Body geschrieben wird."
                        }
                      ]
                    },
                    {
                      type: "textarea",
                      key: "confirmText",
                      label:
                        "Bestätigungstext",
                      rows: 3,
                      description:
                        "Optionaler Text eines Bestätigungsdialogs. Leer lassen, wenn die Aktion ohne Rückfrage ausgeführt werden soll."
                    },
                    {
                      type: "checkbox",
                      key: "promptReason",
                      label:
                        "Grund beim Benutzer abfragen",
                      defaultValue: false,
                      description:
                        "Öffnet vor dem API-Aufruf eine Eingabeaufforderung für einen Grund."
                    },
                    {
                      type: "textfield",
                      key: "reasonPrompt",
                      label:
                        "Text der Grundabfrage",
                      defaultValue:
                        "Bitte geben Sie optional einen Grund ein:",
                      description:
                        "Text der Eingabeaufforderung.",
                      conditional: {
                        json: {
                          "===": [
                            {
                              var:
                                "row.promptReason"
                            },
                            true
                          ]
                        }
                      }
                    },
                    {
                      type: "textfield",
                      key: "reasonField",
                      label: "API-Feld für den Grund",
                      placeholder: "reason",
                      description: "Zum Beispiel Reason für Paket-Vertragsaktionen. Leer verwendet reason."
                    },
                    {
                      type: "textfield",
                      key: "reason",
                      label: "Fester Grund",
                      description:
                        "Optionaler fester Wert, der als reason gesendet wird. Eine Benutzereingabe überschreibt diesen Wert."
                    },
                    {
                      type: "checkbox",
                      key:
                        "reloadAfterAction",
                      label:
                        "Daten nach der Aktion neu laden",
                      defaultValue: true,
                      description:
                        "Lädt die Tabellenzeilen nach erfolgreichem API-Aufruf erneut."
                    },
                    {
                      type: "select",
                      key: "conditionMode",
                      label:
                        "Verknüpfung der Bedingungen",
                      data: {
                        values: [
                          {
                            label:
                              "Alle Bedingungen müssen erfüllt sein",
                            value: "all"
                          },
                          {
                            label:
                              "Mindestens eine Bedingung muss erfüllt sein",
                            value: "any"
                          }
                        ]
                      },
                      defaultValue: "all",
                      description:
                        "Legt fest, wie mehrere Sichtbarkeitsbedingungen verbunden werden."
                    },
                    {
                      type: "editgrid",
                      key: "conditions",
                      label:
                        "Sichtbarkeitsbedingungen",
                      addAnother:
                        "Bedingung hinzufügen",
                      saveRow:
                        "Bedingung übernehmen",
                      removeRow:
                        "Abbrechen",
                      description:
                        "Die Aktion wird nur angezeigt, wenn die Bedingungen für die aktuelle Tabellenzeile erfüllt sind.",
                      components: [
                        {
                          type: "textfield",
                          key: "field",
                          label: "Feld",
                          placeholder:
                            "Status",
                          validate: {
                            required: true
                          },
                          description:
                            "Punktpfad zum Feld innerhalb der Tabellenzeile."
                        },
                        {
                          type: "select",
                          key: "operator",
                          label: "Vergleich",
                          data: {
                            values: [
                              {
                                label:
                                  "Ist gleich",
                                value:
                                  "equals"
                              },
                              {
                                label:
                                  "Ist nicht gleich",
                                value:
                                  "notEquals"
                              },
                              {
                                label:
                                  "Ist einer dieser Werte",
                                value: "in"
                              },
                              {
                                label:
                                  "Ist keiner dieser Werte",
                                value:
                                  "notIn"
                              },
                              {
                                label:
                                  "Hat einen Wert",
                                value:
                                  "hasValue"
                              },
                              {
                                label:
                                  "Hat keinen Wert",
                                value:
                                  "hasNoValue"
                              }
                            ]
                          },
                          defaultValue:
                            "equals"
                        },
                        {
                          type: "textfield",
                          key: "value",
                          label:
                            "Vergleichswert",
                          description:
                            "Bei mehreren Werten diese durch Komma trennen. Für boolesche Werte true oder false verwenden."
                        },
                        {
                          type: "checkbox",
                          key: "ignoreCase",
                          label:
                            "Groß-/Kleinschreibung ignorieren",
                          defaultValue: true
                        }
                      ]
                    },
                    {
                      type: "textarea",
                      key:
                        "visibleWhenJson",
                      label:
                        "Erweiterte Bedingung als JSON",
                      rows: 8,
                      description:
                        "Optionaler Expertenmodus für verschachtelte all-/any-Bedingungen. Überschreibt die einfachen Sichtbarkeitsbedingungen."
                    }
                  ]
                }
              ]
            },
            {
              key: "rowComponents",
              label:
                "Zeilenfelder",
              components: [
                {
                  type:
                    "datagrid",
                  key:
                    "components",
                  label:
                    "Komponenten",
                  reorder: true,
                  description:
                    "Definiert die Felder einer Zeile. Felder mit tableView = true erscheinen als Spalten.",
                  components: [
                    {
                      type:
                        "textfield",
                      key:
                        "label",
                      label:
                        "Label"
                    },
                    {
                      type:
                        "textfield",
                      key:
                        "key",
                      label:
                        "Feldschlüssel"
                    },
                    {
                      type:
                        "select",
                      key:
                        "type",
                      label:
                        "Komponententyp",
                      data: {
                        values: [
                          {
                            label:
                              "Textfeld",
                            value:
                              "textfield"
                          },
                          {
                            label:
                              "Textbereich",
                            value:
                              "textarea"
                          },
                          {
                            label:
                              "E-Mail",
                            value:
                              "email"
                          },
                          {
                            label:
                              "Nummer",
                            value:
                              "number"
                          },
                          {
                            label:
                              "Checkbox",
                            value:
                              "checkbox"
                          },
                          {
                            label:
                              "API Select",
                            value:
                              "apiSelect"
                          },
                          {
                            label:
                              "Select",
                            value:
                              "select"
                          },
                          {
                            label:
                              "Versteckt",
                            value:
                              "hidden"
                          }
                        ]
                      }
                    },
                    {
                      type:
                        "checkbox",
                      key:
                        "tableView",
                      label:
                        "In Tabelle anzeigen",
                      defaultValue: true
                    },
                    {
                      type:
                        "checkbox",
                      key:
                        "disabled",
                      label:
                        "Deaktiviert"
                    },
                    {
                      type:
                        "checkbox",
                      key:
                        "validate.required",
                      label:
                        "Pflichtfeld"
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    };
  };

Formio.Components.addComponent(
  "apiEditGrid",
  ApiEditGridComponent
);
